/**
 * SecretaryPortal.jsx
 *
 * Secretary Portal – event entry list management.
 * Accessible at /secretary
 *
 * Flow:
 *   1. Secretary enters email → magic link sent
 *   2. Magic link redirects to /secretary?token=xxx → verified, session set
 *   3. Secretary sees their events
 *   4. Secretary selects an event → sees entry list
 *   5. Full CRUD on entries via race-number search + form
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import CreatableSelect from 'react-select/creatable';
import '../Secretary.css';
import SubmissionActionDialog from './SubmissionActionDialog';
import EmailEntrantsDialog from './EmailEntrantsDialog';
import SubmissionPrintView from './SubmissionPrintView';
import RefundActionDialog from './RefundActionDialog';
import FinancialDashboard from './FinancialDashboard';

// ─── Constants ────────────────────────────────────────────────────────────────

export const API_KEY = import.meta.env.VITE_API_KEY || '';
// Per-subclass cookie names so different portal roles can stay logged in simultaneously
const _cookieSuffix = (() => {
  const p = new URLSearchParams(window.location.search).get('subclass');
  return p ? `_${p.toLowerCase()}` : '';
})();
const SESSION_COOKIE       = `secretary_session${_cookieSuffix}`;
const SESSION_EMAIL_COOKIE = `secretary_email${_cookieSuffix}`;
const SESSION_ROLE_COOKIE  = `secretary_role${_cookieSuffix}`;
const SAVED_EMAIL_COOKIE   = `secretary_saved_email${_cookieSuffix}`;

// Flags each role may toggle (matches ROLE_EDITABLE_FLAGS on the backend)
const ROLE_EDITABLE_FLAGS = {
  secretary:         ['class_rl', 'class_vintage', 'class_classic', 'class_fe'],
  'event-super-sec': ['class_rl', 'class_vintage', 'class_classic', 'class_fe'],
  fe_secretary:      ['class_fe'],
  'fe-super-sec':    ['class_fe'],
  vc_secretary:      ['class_vintage', 'class_classic'],
  'vc-super-sec':    ['class_vintage', 'class_classic'],
  rl_secretary:      ['class_rl'],
};

const PORTAL_TITLES = {
  secretary:            'Secretary Portal',
  'event-super-sec':    'Secretary Portal (Super)',
  fe_secretary:         'Forgotten Era Secretary Portal',
  'fe-super-sec':       'Forgotten Era Secretary Portal (Super)',
  vc_secretary:         'Vintage-Classic Secretary Portal',
  'vc-super-sec':       'Vintage-Classic Secretary Portal (Super)',
  rl_secretary:         'Road Legal Secretary Portal',
  membership:                'Membership Secretary Portal',
  'membership-super-sec':    'Membership Secretary Portal (Super)',
  financial:                 'Financial Secretary Portal',
  'financial-super-sec':     'Financial Secretary Portal (Super)',
};

// Roles with full event-secretary permissions (create/delete entries, publish)
const FULL_SECRETARY_ROLES = new Set(['secretary', 'event-super-sec']);
const FINANCIAL_ROLES       = new Set(['financial', 'financial-super-sec']);

const isSubclassRole = (role) => !FULL_SECRETARY_ROLES.has(role);

// Online entry only exists for events from the 2027 season onward — matches
// ONLINE_ENTRY_FIRST_YEAR in CalendarEditor.jsx. A 2026-or-earlier event can
// structurally never have any entry_submissions rows (the submit endpoint
// itself rejects them), so there's no reason to even show/fetch that section.
const ONLINE_ENTRY_FIRST_YEAR = 2027;

// Roles where the job is to check subclass eligibility flags (FE/RL/VC), not licences
const CLASS_SUBCLASS_ROLES = new Set(['fe_secretary', 'fe-super-sec', 'vc_secretary', 'vc-super-sec', 'rl_secretary']);

// ─── Cookie helpers ───────────────────────────────────────────────────────────

const setCookie = (name, value, days = 90) => {
  if (days) {
    const exp = new Date(Date.now() + days * 86400000).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)};expires=${exp};path=/`;
  } else {
    // Session cookie — no expires attribute; cleared when browser closes
    document.cookie = `${name}=${encodeURIComponent(value)};path=/`;
  }
};

const getCookie = (name) => {
  const match = document.cookie
    .split(';')
    .map(c => c.trim())
    .find(c => c.startsWith(name + '='));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
};

// ─── API helper ───────────────────────────────────────────────────────────────

export const api = async (path, options = {}, sessionToken = null) => {
  const headers = {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
    ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(path, { ...options, headers });
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    if (res.status === 401 && sessionToken) {
      window.dispatchEvent(new CustomEvent('secretary-session-expired'));
    }
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
};

// ─── Utility ──────────────────────────────────────────────────────────────────

export const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

export const fmtDateTime = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

// refund_status stays 'needed' in the DB/API (matches request_refund's own
// wording) — only the display label reads "Requested", since that's what a
// secretary is actually looking at: a request that hasn't been resolved yet.
const REFUND_STATUS_LABELS = { needed: 'Requested', sent: 'Sent', declined: 'Declined' };
export const refundStatusLabel = (status) => REFUND_STATUS_LABELS[status] || status;

const isFuture = (iso) => {
  if (!iso) return false;
  const today = new Date().toISOString().split("T")[0];
  return iso.split("T")[0] >= today;
};

// ─── BLANK ENTRY FORM ────────────────────────────────────────────────────────

const blankEntry = (raceNumber = '') => ({
  race_number: raceNumber,
  rider_first: '',
  rider_last: '',
  passenger_first: '',
  passenger_last: '',
  type: 'Motorcycle',
  manufacturer: '',
  model: '',
  cc: '',
  class_rl: false,
  class_vintage: false,
  class_classic: false,
  class_fe: false,
  acu_licence_verified: null,
  nhca_membership_verified: null,
  cheque_amount: '',
  bac_reference: '',
  note: '',
});

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Spinner shown while loading data */
export function Spinner() {
  return (
    <div className="sec-loading">
      <div className="spinner" />
      <p>Loading…</p>
    </div>
  );
}

/** Tap/click (i) button that reveals help text on the next line */
function InfoTip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={`infotip-btn${open ? ' infotip-btn--open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Help"
      >i</button>
      {open && <span className="infotip-panel">{text}</span>}
    </>
  );
}

/** Empty state placeholder */
export function EmptyState({ icon, message }) {
  return (
    <div className="sec-empty">
      <div className="sec-empty-icon">{icon}</div>
      <p>{message}</p>
    </div>
  );
}

// ─── Login screen ─────────────────────────────────────────────────────────────

function LoginScreen({  subclass, initialEmail, sessionExpired }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [sending, setSending] = useState(false);

  // initialEmail arrives asynchronously (parent reads cookie in useEffect after first render),
  // so useState above won't capture it — sync it in once it lands.
  useEffect(() => {
    if (initialEmail && !email) setEmail(initialEmail);
  }, [initialEmail]);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const subclassLabels = {
    FE:         'Forgotten Era',
    VC:         'Vintage-Classic',
    RL:         'Road Legal',
    MEMBERSHIP: 'Membership',
    FINANCIAL:  'Financial',
  };
  const roleLabel = subclass ? subclassLabels[subclass] : null;

  const handleSubmit = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }
    setCookie(SAVED_EMAIL_COOKIE, trimmed, 365);
    setSending(true);
    setError(null);
    try {
      await api('/api/secretary/login', {
        method: 'POST',
        body: JSON.stringify({ email: trimmed, ...(subclass ? { subclass } : {}) }),
      });
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="sec-login-wrap">
      <div className="sec-login-card">
        <div className="sec-login-icon">🔐</div>
        <h2>{roleLabel ? `${roleLabel} Secretary Login` : 'Secretary Login'}</h2>
        {sessionExpired ? (
          <p className="subtitle" style={{ color: '#c62828' }}>
            Your session has expired. Request a new login link below.
          </p>
        ) : (
          <p className="subtitle">
            Enter your email address and we&apos;ll send you a one-time login link.
          </p>
        )}

        {!sent ? (
          <>
            <input
              className="sec-email-input"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              autoFocus
            />
            {error && (
              <div className="error-message" style={{ marginBottom: '1rem', textAlign: 'left' }}>
                <p>⚠️ {error}</p>
              </div>
            )}
            <button
              className="sec-primary-btn"
              onClick={handleSubmit}
              disabled={sending}
            >
              {sending ? 'Sending…' : 'Send Login Link →'}
            </button>
          </>
        ) : (
          <div className="sec-sent-message">
            ✅ <strong>Check your inbox.</strong><br />
            If that email is registered, a login link has been sent. It&apos;s valid for 24 hours.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Event list ───────────────────────────────────────────────────────────────

/**
 * Years with at least one event this secretary can see, newest first -
 * null while still loading, [] if the lookup failed.
 */
export function useEventYears(sessionToken) {
  const [years, setYears] = useState(null);
  useEffect(() => {
    api('/api/secretary/events/years', {}, sessionToken)
      .then(data => setYears(data.years || []))
      .catch(() => setYears([]));
  }, [sessionToken]);
  return years;
}

/**
 * The season to open when none was chosen: the current year if this
 * secretary has events in it, otherwise the next season that does (e.g. a
 * secretary whose first event is next year, late in the season), otherwise
 * the current year anyway.
 */
function defaultEventYear(years) {
  if (!years || years.includes(CURRENT_YEAR)) return CURRENT_YEAR;
  const later = years.filter(y => y > CURRENT_YEAR);
  return later.length ? Math.min(...later) : CURRENT_YEAR;
}

export function YearSelect({ years, value, onChange }) {
  // Always offer the current year and the selected one, even with no events.
  const options = Array.from(new Set([CURRENT_YEAR, value, ...(years || [])])).sort((a, b) => b - a);
  return (
    <select
      value={value}
      onChange={e => onChange(Number(e.target.value))}
      aria-label="Season"
      style={{ marginLeft: '0.75rem', padding: '0.25rem 0.5rem', fontSize: '0.95rem' }}
    >
      {options.map(y => <option key={y} value={y}>{y}</option>)}
    </select>
  );
}

function EventList({ sessionToken, role, onSelectEvent, onManageTargets, onManageRecords, onManageFeRecords, initialEventId }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Super-sec roles see every event regardless (is_mine distinguishes ones
  // they're actually the assigned secretary for) - a non-super-sec role's
  // events are already scoped to them, so this toggle has nothing to do.
  const isSuperSec = role?.endsWith('-super-sec');
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  // One season at a time. ?year= keeps the choice across a refresh (and lets
  // the ?event= restore below find an event from a non-current year);
  // without it, defaultEventYear() picks once the secretary's years load.
  const [chosenYear, setChosenYear] = useState(() => {
    const p = Number(new URLSearchParams(window.location.search).get('year'));
    return p >= 1900 && p <= 2100 ? p : null;
  });
  const years = useEventYears(sessionToken);
  const defaultYear = years ? defaultEventYear(years) : null;
  const year = chosenYear ?? defaultYear;

  useEffect(() => {
    if (year == null) return;
    api(`/api/secretary/events?year=${year}`, {}, sessionToken)
      .then(setEvents)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [sessionToken, year]);

  const chooseYear = (y) => {
    if (y === year) return;
    setLoading(true);
    setError(null);
    setChosenYear(y);
    const params = new URLSearchParams(window.location.search);
    if (y === defaultYear) params.delete('year'); else params.set('year', y);
    const qs = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  };

  // Restore the previously-open event from the URL (?event=<id>) after a
  // refresh — otherwise reloading always drops back to this list, which is
  // exactly the "ok, refresh now" moment mid phone-call that this fixes.
  // Guarded with a ref (not just initialEventId itself) since StrictMode
  // double-invokes effects in dev — see CalendarEditor.jsx's verify effect
  // for the same pattern.
  const autoSelectedRef = useRef(false);
  useEffect(() => {
    if (autoSelectedRef.current || !initialEventId || events.length === 0) return;
    const match = events.find(ev => ev.CalendarID === initialEventId);
    if (match) {
      autoSelectedRef.current = true;
      onSelectEvent(match);
    }
  }, [events, initialEventId, onSelectEvent]);

  if (loading) return <Spinner />;
  if (error) return (
    <div className="error-message"><h3>⚠️ Error</h3><p>{error}</p></div>
  );

  const visibleEvents = isSuperSec && showOnlyMine ? events.filter(e => e.is_mine) : events;
  const upcoming = visibleEvents.filter(e => isFuture(e.Date));
  const past = visibleEvents.filter(e => !isFuture(e.Date));

  const canManageTargets   = role === 'vc_secretary' || role === 'vc-super-sec' || role === 'secretary' || role === 'event-super-sec';
  const canManageFeRecords = role === 'fe_secretary' || role === 'fe-super-sec' || role === 'secretary' || role === 'event-super-sec';

  return (
    <div className="sec-section">
      <div className="sec-section-header">
        <h2>📅 Your Events</h2>
        <YearSelect years={years} value={year} onChange={chooseYear} />
        <span className="entry-count-badge">{visibleEvents.length} event{visibleEvents.length !== 1 ? 's' : ''}</span>
        {isSuperSec && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.9rem', marginLeft: '1rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={showOnlyMine} onChange={e => setShowOnlyMine(e.target.checked)} />
            Show only my events
          </label>
        )}
        {(canManageTargets || canManageFeRecords) && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
            {canManageTargets && (
              <>
                <button className="btn-edit" onClick={onManageTargets}>🎯 VC Targets</button>
                <button className="btn-edit" onClick={onManageRecords}>🏆 VC Records</button>
              </>
            )}
            {canManageFeRecords && (
              <button className="btn-edit" onClick={onManageFeRecords}>🏅 FE Records</button>
            )}
          </div>
        )}
      </div>

      {visibleEvents.length === 0 && (
        <EmptyState icon="📭" message={
          showOnlyMine
            ? `None of the ${year} events are assigned to your email address.`
            : `No ${year} events are assigned to your email address.`
        } />
      )}

      {upcoming.length > 0 && (
        <>
          <p className="form-section-label" style={{ marginBottom: '0.75rem' }}>Upcoming</p>
          <div className="event-cards-grid" style={{ marginBottom: '1.5rem' }}>
            {upcoming.map(ev => (
              <EventCard key={ev.CalendarID} event={ev} onSelect={onSelectEvent} future />
            ))}
          </div>
        </>
      )}

      {past.length > 0 && (
        <>
          <p className="form-section-label" style={{ marginBottom: '0.75rem' }}>Past Events</p>
          <div className="event-cards-grid">
            {past.map(ev => (
              <EventCard key={ev.CalendarID} event={ev} onSelect={onSelectEvent} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EventCard({ event, onSelect, future }) {
  const classes = [
    event.open      && { label: 'Open',      cls: '' },
    event.roadlegal && { label: 'Road Legal', cls: 'rl' },
    event.vc        && { label: 'Vintage/Classic', cls: 'vc' },
    event['forgotten era'] && { label: 'Forgotten Era', cls: 'fe' },
    event.restricted && { label: 'Restricted', cls: 'res' },
  ].filter(Boolean);

  return (
    <div
      className={`event-card ${future ? 'future' : 'past'}`}
      onClick={() => onSelect(event)}
    >
      <div className="event-card-hill">{event.Hill}</div>
      <div className="event-card-date">{fmtDate(event.Date)}</div>
      {classes.length > 0 && (
        <div className="event-card-classes">
          {classes.map(c => (
            <span key={c.label} className={`class-pill ${c.cls}`}>{c.label}</span>
          ))}
        </div>
      )}
      {event.closingdate && (
        <div style={{ marginTop: '0.6rem', fontSize: '0.8rem', color: 'var(--text-light)' }}>
          Closes: {fmtDate(event.closingdate)}
        </div>
      )}
    </div>
  );
}

// ─── Entry list ───────────────────────────────────────────────────────────────

function EntryList({ event, sessionToken, role = 'secretary', onBack }) {
  const eventYear = event.Date ? new Date(event.Date.split('T')[0] + 'T00:00:00').getFullYear() : null;
  const showSubmissions = !isSubclassRole(role) && eventYear !== null && eventYear >= ONLINE_ENTRY_FIRST_YEAR;

  const [entries, setEntries]   = useState([]);
  const [error, setError]       = useState(null);
  const [filter, setFilter]     = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);   // entry to edit
  const [deleting, setDeleting] = useState(null);   // entry to confirm-delete
  const [published, setPublished]     = useState(event.published || false);
  const [publishing, setPublishing]   = useState(false);
  // Shown before publishing when there's a reason to wait: online entries
  // not yet accepted ({received, pending} counts) and/or a restricted
  // (possibly oversubscribed) event. null = no warning open.
  const [publishCheck, setPublishCheck] = useState(null);
  const [permitNo, setPermitNo]         = useState(event.acu_permit || null);
  const [showPermitPrompt, setShowPermitPrompt] = useState(false);
  const [pendingDocType, setPendingDocType] = useState('checklist');

  const [showCopyModal, setShowCopyModal] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [sortBy, setSortBy]           = useState('race_number');
  const [prices, setPrices]           = useState({});
  const [bacsWarnOpen, setBacsWarnOpen] = useState(null);

  // Entry still held for committee review (submission_status='pending') —
  // clicking its badge fetches the full originating submission so the same
  // decide dialog used from "New Entries Received" can be reused here too.
  const [decidingEntry, setDecidingEntry]           = useState(null);
  const [decidingSubmission, setDecidingSubmission] = useState(null);
  const [decidingAction, setDecidingAction]         = useState(null); // action picked from the pill's own dropdown, if any
  const [printingSubmissionId, setPrintingSubmissionId] = useState(null);
  // Raise a refund request — reachable from any entry with a linked
  // submission, regardless of submission_status (e.g. an overpayment on a
  // still-'accepted' entry). Independent of the decide dialog above.
  const [requestingRefundFor, setRequestingRefundFor] = useState(null);

  const openPendingDialog = async (entry, action = null) => {
    setDecidingEntry(entry);
    setDecidingAction(action);
    try {
      const subs = await api(`/api/secretary/entries/${event.CalendarID}/submissions`, {}, sessionToken);
      const match = subs.find(s => s.id === entry.submission_id);
      if (!match) {
        setError('Could not find the originating submission for this entry.');
        setDecidingEntry(null);
        return;
      }
      setDecidingSubmission(match);
    } catch (err) {
      setError(err.message);
      setDecidingEntry(null);
    }
  };
  const loadEntries = useCallback(async (silent = false) => {
    if (!silent) setInitialLoad(true);
    try {
      const isFullSecRole = role === 'secretary' || role === 'event-super-sec';
      const [data, priceData] = await Promise.all([
        api(`/api/secretary/entries/${event.CalendarID}`, {}, sessionToken),
        isFullSecRole ? api(`/api/secretary/events/${event.CalendarID}/prices`, {}, sessionToken) : Promise.resolve(null),
      ]);
      setEntries(data);
      if (priceData) setPrices(priceData);
    } catch (err) {
      setError(err.message);
    } finally {
      setInitialLoad(false);
    }
  }, [event.CalendarID, sessionToken, role]);

  useEffect(() => { loadEntries(false); }, [loadEntries]);

  // Before publishing: count online entries still Received or Held, fresh
  // from the server (the submissions list below may be out of date). If
  // the count can't be fetched, publishing goes ahead as before.
  const requestPublish = async () => {
    let received = 0, pending = 0;
    if (showSubmissions) {
      setPublishing(true);
      try {
        const subs = await api(`/api/secretary/entries/${event.CalendarID}/submissions`, {}, sessionToken);
        received = subs.filter(s => s.status === 'received').length;
        pending = subs.filter(s => s.status === 'pending').length;
      } catch { /* no count: no entries warning */ }
      setPublishing(false);
    }
    if (received || pending || event.restricted) {
      setPublishCheck({ received, pending, restricted: !!event.restricted });
    } else {
      handlePublish(true);
    }
  };

  const handlePublish = async (newState) => {
    setPublishing(true);
    try {
      const result = await api(
        `/api/secretary/events/${event.CalendarID}/publish`,
        { method: 'POST', body: JSON.stringify({ published: newState }) },
        sessionToken
      );
      setPublished(result.published);
    } catch (err) {
      setError(err.message);
    } finally {
      setPublishing(false);
    }
  };

const handlePermitSave = async (val) => {
    try {
      const result = await api(
        `/api/secretary/events/${event.CalendarID}/acu-permit`,
        { method: 'PATCH', body: JSON.stringify({ acu_permit: val }) },
        sessionToken
      );
      setPermitNo(result.acu_permit);
    } catch (err) {
      setError(err.message);
    }
  };

const handleExport = () => {
  const url = `/api/secretary/entries/${event.CalendarID}/export?token=${encodeURIComponent(sessionToken)}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const handleDocDownload = (type) => {
  const url = `/api/secretary/entries/${event.CalendarID}/${type}?token=${encodeURIComponent(sessionToken)}`;
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await api(
        `/api/secretary/entries/${event.CalendarID}/${deleting.race_number}`,
        { method: 'DELETE' },
        sessionToken
      );
      setDeleting(null);
      loadEntries(true);
    } catch (err) {
      setError(err.message);
      setDeleting(null);
    }
  };

  const editableFlags = ROLE_EDITABLE_FLAGS[role] || [];
  const isMembershipRole = role === 'membership' || role === 'membership-super-sec';
  const isFullSec = role === 'event-super-sec' || role === 'secretary';

  const handleFlagToggle = async (entry, flagKey) => {
    try {
      const updated = { ...entry, [flagKey]: !entry[flagKey] };
      const result = await api(
        `/api/secretary/entries/${event.CalendarID}/${entry.race_number}`,
        { method: 'PUT', body: JSON.stringify(updated) },
        sessionToken
      );
      setEntries(prev => prev.map(e => e.race_number === entry.race_number ? result : e));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleMembershipToggle = async (entry, field) => {
    try {
      // Each field cycles independently: null (?) -> true (tick) -> false (cross) -> null.
      // The field not being clicked is resent at its current tri-state so it's left alone.
      const acuCurrent  = triState(entry.acu_licence_verified, entry.acu_licence_ok);
      const nhcaCurrent = triState(entry.nhca_membership_verified, entry.nhca_membership_ok);
      const payload = {
        acu_licence_state:     field === 'acu_licence_verified'     ? nextTriState(acuCurrent)  : acuCurrent,
        nhca_membership_state: field === 'nhca_membership_verified' ? nextTriState(nhcaCurrent) : nhcaCurrent,
        note: entry.note || null,
      };
      const result = await api(
        `/api/secretary/entries/${event.CalendarID}/${entry.race_number}`,
        { method: 'PUT', body: JSON.stringify(payload) },
        sessionToken
      );
      setEntries(prev => prev.map(e => e.race_number === entry.race_number ? result : e));
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePaymentUpdate = async (entry, field, value) => {
    const payload = {
      race_number:   entry.race_number,
      rider_first:   entry.rider_first,
      rider_last:    entry.rider_last,
      cheque_amount: field === 'cheque_amount' ? value : (entry.cheque_amount ?? null),
      bac_reference: field === 'bac_reference' ? value : (entry.bac_reference ?? null),
    };
    if (field === 'bacs_amount') {
      payload.bacs_amount = value;
    } else if (field === 'bac_reference' && value && entry.bacs_amount == null) {
      const autoFee = computeEntryFee(entry, entries, prices);
      console.log('[bacs auto-fill]', { ref: value, bacs_amount: entry.bacs_amount, autoFee, prices });
      if (autoFee != null) payload.bacs_amount = autoFee;
    }
    try {
      await api(
        `/api/secretary/entries/${event.CalendarID}/payment`,
        { method: 'PUT', body: JSON.stringify(payload) },
        sessionToken
      );
      setEntries(prev => prev.map(e =>
        e.race_number === entry.race_number
          ? {
              ...e,
              cheque_amount: payload.cheque_amount,
              bac_reference: payload.bac_reference,
              ...('bacs_amount' in payload ? { bacs_amount: payload.bacs_amount } : {}),
            }
          : e
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleRefTotalUpdate = async (entry, newTotal) => {
    try {
      await api(
        `/api/secretary/entries/${event.CalendarID}/bacs-ref-total`,
        { method: 'PUT', body: JSON.stringify({ bac_reference: entry.bac_reference, bacs_ref_total: newTotal }) },
        sessionToken
      );
      const ref = entry.bac_reference;
      setEntries(prev => prev.map(e =>
        e.bac_reference === ref ? { ...e, bacs_ref_total: newTotal } : e
      ));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleNoteUpdate = async (entry, newNote) => {
    try {
      const result = await api(
        `/api/secretary/entries/${event.CalendarID}/${entry.race_number}`,
        { method: 'PUT', body: JSON.stringify({ ...entry, note: newNote || null }) },
        sessionToken
      );
      setEntries(prev => prev.map(e => e.race_number === entry.race_number ? result : e));
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePriceSave = async (field, val) => {
    const payload = {
      first_bike_price:  prices.first_bike  ?? null,
      second_bike_price: prices.second_bike ?? null,
      first_3w_price:    prices.first_3w    ?? null,
      second_3w_price:   prices.second_3w   ?? null,
      [field]: val,
    };
    try {
      await api(
        `/api/secretary/events/${event.CalendarID}/prices`,
        { method: 'PUT', body: JSON.stringify(payload) },
        sessionToken
      );
      await loadEntries(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const filtered = entries.filter(e => {
    const q = filter.toLowerCase();
    return (
      !q ||
      String(e.race_number).includes(q) ||
      `${e.rider_first} ${e.rider_last}`.toLowerCase().includes(q) ||
      `${e.manufacturer} ${e.model}`.toLowerCase().includes(q)
    );
  });

  const sorted = sortBy === 'name'
    ? [...filtered].sort((a, b) => {
        const la = (a.rider_last || '').toLowerCase();
        const lb = (b.rider_last || '').toLowerCase();
        if (la !== lb) return la < lb ? -1 : 1;
        return (a.rider_first || '').toLowerCase() < (b.rider_first || '').toLowerCase() ? -1 : 1;
      })
    : filtered;

  const existingNumbers = new Set(entries.map(e => e.race_number));

  const nameCounts = {};
  for (const e of entries) {
    const name = [e.rider_first, e.rider_last].filter(Boolean).join(' ');
    if (name) nameCounts[name] = (nameCounts[name] || 0) + 1;
  }
  const warnNames = new Set(Object.keys(nameCounts).filter(n => nameCounts[n] > 2));

  const pricesSet = isFullSec && (prices.first_bike != null || prices.first_3w != null);
  const totalOwed        = pricesSet ? entries.reduce((s, e) => s + (computeEntryFee(e, entries, prices) || 0), 0) : 0;
  // The day licence / Give It A Go and day membership parts of totalOwed,
  // shown as their own sub-totals.
  const totalLicence     = pricesSet ? +entries.reduce((s, e) => s + (e.licence_fee || 0), 0).toFixed(2) : 0;
  const totalMembership  = pricesSet ? +entries.reduce((s, e) => s + (e.membership_fee || 0), 0).toFixed(2) : 0;
  const totalCheque = isFullSec ? entries.reduce((s, e) => s + (e.cheque_amount || 0), 0) : 0;

  let totalBacsConfirmed = 0;
  if (isFullSec) {
    const seenRefs = new Set();
    for (const e of entries) {
      if (e.bac_reference && e.bacs_actual_amount != null && !seenRefs.has(e.bac_reference)) {
        seenRefs.add(e.bac_reference);
        totalBacsConfirmed += e.bacs_actual_amount;
      }
    }
  }

  // Deduplicated by submission_id, not by row — a single submission can
  // cover multiple machines/rows (bike+sidecar combo), and refund_amount is
  // tracked once per submission, not once per row.
  const totalRefunded = (() => {
    const seen = new Set();
    let sum = 0;
    for (const e of entries) {
      if (e.submission_id && e.submission_refund_status === 'sent' && e.submission_refund_amount != null && !seen.has(e.submission_id)) {
        seen.add(e.submission_id);
        sum += e.submission_refund_amount;
      }
    }
    return sum;
  })();

  const totalOutstanding = totalOwed - totalCheque - totalBacsConfirmed + totalRefunded;

  const refFeeMap = {};
  if (isFullSec && pricesSet) {
    for (const e of entries) {
      if (e.bac_reference) {
        const fee = computeEntryFee(e, entries, prices);
        if (fee != null) refFeeMap[e.bac_reference] = +((refFeeMap[e.bac_reference] || 0) + fee).toFixed(2);
      }
    }
  }

  // Same submission-id dedup as totalRefunded above, scoped per bac_reference
  // group — a coverage-group payer's own refund should only count once
  // toward that shared reference's adjustment, not once per covered row.
  const refRefundMap = {};
  if (isFullSec) {
    const seenPerRef = {};
    for (const e of entries) {
      if (e.bac_reference && e.submission_id && e.submission_refund_status === 'sent' && e.submission_refund_amount != null) {
        seenPerRef[e.bac_reference] = seenPerRef[e.bac_reference] || new Set();
        if (!seenPerRef[e.bac_reference].has(e.submission_id)) {
          seenPerRef[e.bac_reference].add(e.submission_id);
          refRefundMap[e.bac_reference] = +((refRefundMap[e.bac_reference] || 0) + e.submission_refund_amount).toFixed(2);
        }
      }
    }
  }

  const refSeqMap = {};
  if (isFullSec) {
    const refGroups = {};
    for (const e of entries) {
      if (e.bac_reference) {
        if (!refGroups[e.bac_reference]) refGroups[e.bac_reference] = [];
        refGroups[e.bac_reference].push(e.race_number);
      }
    }
    for (const [ref, raceNums] of Object.entries(refGroups)) {
      if (raceNums.length > 1) {
        const sorted = [...raceNums].sort((a, b) => a - b);
        refSeqMap[ref] = {};
        sorted.forEach((rn, i) => { refSeqMap[ref][rn] = i + 1; });
      }
    }
  }

  return (
    <>
      <div className="sec-section">
        <div className="sec-section-header">
          <div>
            <button
              className="secretary-nav-btn"
              onClick={onBack}
              style={{ marginBottom: '0.5rem', display: 'inline-block' }}
            >
              ← Back to Events
            </button>
            <h2>🏁 {event.eventname || `${event.Hill} – ${fmtDate(event.Date)}`}</h2>
            <div style={{ fontSize: '0.88rem', color: '#555', marginTop: '0.2rem' }}>
              ACU Permit:{' '}
              {(role === 'secretary' || role === 'event-super-sec')
                ? <InlineEditCell value={permitNo} onSave={handlePermitSave} placeholder="not set" />
                : <span>{permitNo || '—'}</span>
              }
            </div>
          </div>
<div className="event-action-buttons" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            {!isSubclassRole(role) && (
              <>
                {/* Status and action are shown separately: a single toggle
                    labelled with its state left people unsure whether
                    clicking it would publish or unpublish. */}
                <span
                  title={published
                    ? 'Anyone can see this entry list on the public calendar (Entries → View) and at the shareable link.'
                    : 'Only secretaries can see this entry list. The public calendar shows no View link for it yet.'}
                  style={{
                    fontSize: '0.88rem', fontWeight: 600, padding: '0.35rem 0.6rem', borderRadius: 4,
                    background: published ? '#e8f5e9' : '#fdecea',
                    color: published ? '#2e7d32' : '#c62828',
                  }}
                >
                  {published ? '✅ Entry list is public' : '🔒 Entry list is hidden'}
                </span>
                <button
                  className="btn-add-entry"
                  title={published
                    ? 'Hide the entry list from the public. Your entries are kept; you can publish again at any time.'
                    : 'Show the entry list to the public on the calendar and at the shareable link. You can unpublish again at any time.'}
                  onClick={() => (published ? handlePublish(false) : requestPublish())}
                  disabled={publishing}
                  style={published
                    ? { background: 'white', color: '#c62828', border: '1px solid #c62828' }
                    : { background: '#2e7d32', color: 'white', border: 'none' }}
                >
                  {publishing ? '…' : published ? 'Unpublish' : 'Publish entry list'}
                </button>
                <a
                  href={window.location.origin + '/entries/' + event.CalendarID}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-add-entry"
                  style={{ background: 'var(--primary-color)', textDecoration: 'none', fontSize: '0.9rem' }}
                >
                  🔗 Shareable Link
                </a>
                <button
                  className="btn-add-entry"
                  onClick={() => handleExport()}
                  style={{ background: '#5c6bc0', color: 'white' }}
                >
                  ⬇ Export CSV
                </button>
                <button
                  className="btn-add-entry"
                  onClick={() => {
                    setPendingDocType('signonform');
                    if (!permitNo) setShowPermitPrompt(true);
                    else handleDocDownload('signonform');
                  }}
                  style={{ background: '#1565c0', color: 'white' }}
                >
                  ⬇ Sign-On Form
                </button>
                <button
                  className="btn-add-entry"
                  onClick={() => {
                    setPendingDocType('checklist');
                    if (!permitNo) setShowPermitPrompt(true);
                    else handleDocDownload('checklist');
                  }}
                  style={{ background: '#2e7d32', color: 'white' }}
                >
                  ⬇ Check List
                </button>
                <button
                  className="btn-add-entry"
                  title="Everything for the day in one zip file, in case the internet fails: all entry forms, the entry list, the sign-on form, the check list and the CSV export. Download it the evening before."
                  onClick={() => {
                    setPendingDocType('backup');
                    if (!permitNo) setShowPermitPrompt(true);
                    else handleDocDownload('backup');
                  }}
                  style={{ background: '#6d4c41', color: 'white' }}
                >
                  ⬇ Backup (zip)
                </button>

                {showPermitPrompt && (
                  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
                       onKeyDown={e => { if (e.key === 'Escape') setShowPermitPrompt(false); }}>
                    <div style={{ background: 'white', borderRadius: '8px', padding: '1.5rem', maxWidth: '360px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                      <p style={{ marginTop: 0, fontWeight: 600 }}>⚠️ Warning: ACU permit number missing</p>
                      <p style={{ fontSize: '0.9rem', color: '#555' }}>
                        It will be blank on the sign-on form and check list. To add it, click Cancel, then click the dash next to
                        &quot;ACU Permit&quot; at the top of this page and type it in.
                      </p>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <button className="btn-add-entry" style={{ background: '#aaa' }} onClick={() => setShowPermitPrompt(false)}>Cancel</button>
                        <button className="btn-add-entry" autoFocus onClick={() => { setShowPermitPrompt(false); handleDocDownload(pendingDocType); }}>Continue</button>
                      </div>
                    </div>
                  </div>
                )}
                {!showSubmissions && (
                  <button
                    className="btn-add-entry"
                    onClick={() => setShowCopyModal(true)}
                    style={{ background: '#37474f', color: 'white' }}
                  >
                    ⎘ Copy from Event
                  </button>
                )}
                {!showSubmissions && (
                  <button
                    className="btn-add-entry"
                    onClick={() => { setEditing(null); setShowForm(true); }}
                  >
                    + Add Entry
                  </button>
                )}
              </>
            )}
          </div>
        </div>

        {showSubmissions && (
          <SubmissionsList event={event} sessionToken={sessionToken} onEntryListChanged={() => loadEntries(true)} />
        )}

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            <p>⚠️ {error}</p>
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        <div className="entries-toolbar">
          <input
            className="entries-search"
            type="text"
            placeholder="Search the table below by number, name, or bike"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          <span className="entry-count-badge">
            {CLASS_SUBCLASS_ROLES.has(role) ? (() => {
              const classCount = entries.filter(e =>
                (role === 'fe_secretary' || role === 'fe-super-sec') ? e.class_fe :
                role === 'rl_secretary' ? e.class_rl :
                /* vc */ e.class_vintage || e.class_classic
              ).length;
              return `${classCount} / ${entries.length}`;
            })() : `${filtered.length} / ${entries.length} entr${entries.length !== 1 ? 'ies' : 'y'}`}
          </span>
        </div>

        {initialLoad ? (
          <Spinner />
        ) : entries.length === 0 ? (
          <EmptyState
            icon="📋"
            message={showSubmissions ? 'Awaiting first entry — none submitted yet.' : "No entries yet. Click 'Add Entry' to get started."}
          />
        ) : (
          <>
          {!CLASS_SUBCLASS_ROLES.has(role) && (() => {
            const missingRiders = new Set(
              entries
                .filter(e => !e.acu_licence_verified || !e.nhca_membership_verified)
                .map(e => `${e.rider_first || ''}|${e.rider_last || ''}`)
            );
            return missingRiders.size > 0 ? (
              <p style={{ marginBottom: '0.5rem', color: '#fb6107', fontWeight: 600 }}>
                ⚠️ {missingRiders.size} rider{missingRiders.size !== 1 ? 's' : ''} with missing ACU / NHCA verification
              </p>
            ) : null;
          })()}
          {isFullSec && (() => {
            const unpaidRiders = new Set(
              entries
                .filter(e => !e.cheque_amount && !e.bacs_amount)
                .map(e => `${e.rider_first || ''}|${e.rider_last || ''}`)
            );
            return unpaidRiders.size > 0 ? (
              <p style={{ marginBottom: '0.5rem', color: '#c62828', fontWeight: 600 }}>
                ⚠️ {unpaidRiders.size} rider{unpaidRiders.size !== 1 ? 's' : ''} with no payment recorded
              </p>
            ) : null;
          })()}
          {isFullSec && (
            <div style={{ background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem 1rem', marginBottom: '1rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center', fontSize: '0.88rem' }}>
              <strong>Entry Fees (£):</strong>
              <span>1st Bike: <InlineEditCell value={prices.first_bike} inputType="number" prefix="£" placeholder="0.00" onSave={v => handlePriceSave('first_bike_price', v)} /></span>
              <span>2nd Bike: <InlineEditCell value={prices.second_bike} inputType="number" prefix="£" placeholder="0.00" onSave={v => handlePriceSave('second_bike_price', v)} /></span>
              <span>1st 3-Wheeler: <InlineEditCell value={prices.first_3w} inputType="number" prefix="£" placeholder="0.00" onSave={v => handlePriceSave('first_3w_price', v)} /></span>
              <span>2nd 3-Wheeler: <InlineEditCell value={prices.second_3w} inputType="number" prefix="£" placeholder="0.00" onSave={v => handlePriceSave('second_3w_price', v)} /></span>
            </div>
          )}
          <div className="entries-table-wrap">
            <table className="entries-table">
              <thead>
                <tr>
                  <th></th>
                  <th onClick={() => setSortBy('race_number')} title="Sort by race number" style={{ cursor: 'pointer', userSelect: 'none' }}>#{sortBy === 'race_number' ? ' ▲' : ''}</th>
                  <th onClick={() => setSortBy('name')} title="Sort by name" style={{ cursor: 'pointer', userSelect: 'none' }}>Rider{sortBy === 'name' ? ' ▲' : ''}</th>
                  <th>Passenger</th>
                  <th>Type</th>
                  <th>Bike</th>
                  <th>CC</th>
                  {!isMembershipRole && <th>RL</th>}
                  {!isMembershipRole && <th>Vtg</th>}
                  {!isMembershipRole && <th>Cls</th>}
                  {!isMembershipRole && <th>FE</th>}
                  <th title="ACU Licence verified">ACU</th>
                  <th title="NHCA Membership verified">NHCA</th>
                  <th title="Day licence or Give It A Go (riders without an ACU licence), and Memb: a day NHCA membership">Licence</th>
                  <th className="note-col">Note</th>
                  {isFullSec && <th title="BACS payment reference">BACS Ref</th>}
                  {isFullSec && <th title="Entry fee">Fee</th>}
                  {isFullSec && <th title="Cheque/PayPal/Cash amount received">Cheque/PP/Cash</th>}
                  {isFullSec && <th title="BACS amount received (entered by financial secretary)">BACS Amount</th>}
                  {isFullSec && <th title="Amount refunded so far (cheque/BACS amounts are never adjusted down — this is tracked separately)">Refunded</th>}
                  {isFullSec && <th title="Fee minus all payments received, plus back any amount refunded">Adjustment</th>}
                </tr>
              </thead>
              <tbody>
                {sorted.map(e => (
                  <tr
                    key={e.race_number}
                    style={(() => {
                      if (CLASS_SUBCLASS_ROLES.has(role)) {
                        if ((role === 'fe_secretary' || role === 'fe-super-sec') && e.class_fe)
                          return { background: 'rgba(255, 152, 0, 0.18)' };
                        if (role === 'rl_secretary' && e.class_rl)
                          return { background: 'rgba(0, 137, 123, 0.15)' };
                        if ((role === 'vc_secretary' || role === 'vc-super-sec') && (e.class_vintage || e.class_classic))
                          return { background: 'rgba(103, 58, 183, 0.13)' };
                        return undefined;
                      }
                      if (!e.acu_licence_verified || !e.nhca_membership_verified)
                        return { background: 'rgba(251, 97, 7, 0.12)' };
                      if (isFullSec && !e.cheque_amount && !e.bacs_amount)
                        return { background: 'rgba(198, 40, 40, 0.08)' };
                      return undefined;
                    })()}
                  >
                    <td>
                      {!isSubclassRole(role) && (
                        <div className="row-actions">
                          <button
                            className="btn-edit"
                            onClick={() => { setEditing(e); setShowForm(true); }}
                          >
                            Edit
                          </button>
                          {!showSubmissions && (
                            <button
                              className="btn-delete"
                              onClick={() => setDeleting(e)}
                            >
                              Del
                            </button>
                          )}
                          {e.submission_id && (
                            <button
                              className="btn-edit"
                              onClick={() => setPrintingSubmissionId(e.submission_id)}
                              title="Print this entry as the entrant would have printed it"
                            >
                              🖨️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="race-num-cell">
                      {e.race_number}
                      {e.submission_status === 'pending' && (
                        <DecidePill
                          label="Pending"
                          className="status-pill status-pending"
                          style={{ display: 'block', marginTop: '3px', fontSize: '0.65rem', padding: '0.15rem 0.4rem' }}
                          options={[
                            { action: 'reject', label: 'Reject', danger: true },
                            { action: 'accept', label: 'Accept & Notify' },
                          ]}
                          onPick={(action) => openPendingDialog(e, action)}
                        />
                      )}
                      {e.submission_status !== 'pending' && (
                        isFullSec && e.submission_status === 'accepted' && e.submission_id ? (
                          <DecidePill
                            label="✓ Accepted"
                            className="status-pill status-accepted"
                            style={{ display: 'block', marginTop: '3px', fontSize: '0.65rem', padding: '0.15rem 0.4rem', background: 'none', color: '#2e7d32', border: 'none', cursor: 'pointer' }}
                            options={[{ action: 'cancel', label: 'Cancel Entry', danger: true }]}
                            onPick={(action) => openPendingDialog(e, action)}
                          />
                        ) : (
                          <div
                            style={{ fontSize: '0.72rem', color: '#2e7d32', marginTop: '2px' }}
                            title={e.submission_status === 'accepted' ? 'Entered online and accepted' : 'Accepted'}
                          >
                            ✓ Accepted
                          </div>
                        )
                      )}
                      {isFullSec && e.submission_id && (
                        <button
                          type="button"
                          className="btn-edit"
                          style={{ display: 'block', marginTop: '2px', fontSize: '0.62rem', padding: '0.1rem 0.35rem' }}
                          onClick={() => setRequestingRefundFor(e)}
                          title="Request a refund for this entry"
                        >
                          Refund…
                        </button>
                      )}
                      {e.submission_refund_status && (
                        <div
                          className={`status-pill status-refund-${e.submission_refund_status}`}
                          style={{ display: 'inline-block', marginTop: '2px', fontSize: '0.62rem', padding: '0.1rem 0.35rem' }}
                          title={`Refund ${refundStatusLabel(e.submission_refund_status)}`}
                        >
                          Refund: {refundStatusLabel(e.submission_refund_status)}
                        </div>
                      )}
                    </td>
                    <td>
                      {warnNames.has([e.rider_first, e.rider_last].filter(Boolean).join(' ')) && (
                        <span title="More than 2 entries for this name – possible name clash" style={{ color: 'orange', marginRight: '4px' }}>⚠</span>
                      )}
                      {[e.rider_first, e.rider_last].filter(Boolean).join(' ') || '—'}
                    </td>
                    <td>{[e.passenger_first, e.passenger_last].filter(Boolean).join(' ') || '—'}</td>
                    <td>{e.type || '—'}</td>
                    <td>{[e.manufacturer, e.model].filter(Boolean).join(' ') || '—'}</td>
                    <td>{e.cc || '—'}</td>
                    {!isMembershipRole && <td><FlagCell v={e.class_rl}      flagKey="class_rl"      editable={editableFlags.includes('class_rl')}      onToggle={() => handleFlagToggle(e, 'class_rl')} /></td>}
                    {!isMembershipRole && <td><FlagCell v={e.class_vintage}  flagKey="class_vintage"  editable={editableFlags.includes('class_vintage')}  onToggle={() => handleFlagToggle(e, 'class_vintage')} /></td>}
                    {!isMembershipRole && <td><FlagCell v={e.class_classic}  flagKey="class_classic"  editable={editableFlags.includes('class_classic')}  onToggle={() => handleFlagToggle(e, 'class_classic')} /></td>}
                    {!isMembershipRole && <td><FlagCell v={e.class_fe}       flagKey="class_fe"       editable={editableFlags.includes('class_fe')}       onToggle={() => handleFlagToggle(e, 'class_fe')} /></td>}
                    <td><TriStateFlagCell dateVal={e.acu_licence_verified}     okVal={e.acu_licence_ok}     editable={isMembershipRole} onToggle={() => handleMembershipToggle(e, 'acu_licence_verified')} /></td>
                    <td><TriStateFlagCell dateVal={e.nhca_membership_verified} okVal={e.nhca_membership_ok} editable={isMembershipRole} onToggle={() => handleMembershipToggle(e, 'nhca_membership_verified')} /></td>
                    <td>{licenceLabel(e)}</td>
                    <td className="note-col"><NoteCell value={e.note} label={`#${e.race_number} ${[e.rider_first, e.rider_last].filter(Boolean).join(' ')}`} onSave={newNote => handleNoteUpdate(e, newNote)} /></td>
                    {isFullSec && (
                      <td>
                        <InlineEditCell
                          value={e.bac_reference}
                          placeholder="Ref"
                          onSave={val => handlePaymentUpdate(e, 'bac_reference', val)}
                        />
                        {e.bac_reference && refSeqMap[e.bac_reference]?.[e.race_number] != null && (
                          <span style={{ color: '#888', fontSize: '0.82em', marginLeft: '3px' }}>
                            ({refSeqMap[e.bac_reference][e.race_number]})
                          </span>
                        )}
                      </td>
                    )}
                    {isFullSec && (
                      <td style={{ color: '#555', fontSize: '0.85rem' }}>
                        {fmtCurrency(computeEntryFee(e, entries, prices))}
                      </td>
                    )}
                    {isFullSec && (
                      <td>
                        <InlineEditCell
                          value={e.cheque_amount}
                          inputType="number"
                          prefix="£"
                          placeholder="0.00"
                          onSave={val => handlePaymentUpdate(e, 'cheque_amount', val)}
                        />
                      </td>
                    )}
                    {isFullSec && (
                      <td>
                        <InlineEditCell
                          value={e.bacs_amount}
                          inputType="number"
                          prefix="£"
                          placeholder="0.00"
                          onSave={val => handlePaymentUpdate(e, 'bacs_amount', val)}
                        />
                        {hasSharedRefTotal(e, entries) && (
                          <div style={{ fontSize: '0.8rem', color: '#888', marginTop: '2px' }}>
                            {'of '}
                            <span title="Enter the total amount transferred as stated on the entry form">
                              <InlineEditCell
                                value={e.bacs_ref_total}
                                inputType="number"
                                prefix="£"
                                placeholder="Stated Total"
                                onSave={val => handleRefTotalUpdate(e, val)}
                              />
                            </span>
                            {e.bacs_ref_total != null && refFeeMap[e.bac_reference] != null &&
                             Math.abs(e.bacs_ref_total - refFeeMap[e.bac_reference]) > 0.005 && (
                              <span style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                  type="button"
                                  onClick={() => setBacsWarnOpen(bacsWarnOpen === e.race_number ? null : e.race_number)}
                                  style={{ background: 'none', border: 'none', padding: 0, color: 'orange', cursor: 'pointer', fontSize: '1em', marginLeft: '4px' }}
                                  title="Click for details"
                                >⚠</button>
                                {bacsWarnOpen === e.race_number && (
                                  <div style={{ position: 'absolute', zIndex: 200, background: '#fff', border: '1px solid #bbb', borderRadius: '4px', padding: '10px 14px', minWidth: '230px', boxShadow: '0 2px 8px rgba(0,0,0,0.18)', top: '1.6em', left: 0, fontSize: '0.82rem', color: '#333', lineHeight: 1.6 }}>
                                    <div><strong>Stated:</strong> £{Number(e.bacs_ref_total).toFixed(2)}</div>
                                    <div><strong>Expected fees:</strong> £{Number(refFeeMap[e.bac_reference]).toFixed(2)}</div>
                                    {e.bacs_actual_amount != null && (
                                      <>
                                        <div style={{ marginTop: '6px' }}><strong>Actual received:</strong> £{Number(e.bacs_actual_amount).toFixed(2)}</div>
                                        {Math.abs(e.bacs_actual_amount - e.bacs_ref_total) > 0.005 && (
                                          <button
                                            type="button"
                                            onClick={async () => { await handleRefTotalUpdate(e, e.bacs_actual_amount); setBacsWarnOpen(null); }}
                                            style={{ marginTop: '6px', fontSize: '0.82rem', padding: '3px 10px', cursor: 'pointer' }}
                                          >Update stated to £{Number(e.bacs_actual_amount).toFixed(2)}</button>
                                        )}
                                      </>
                                    )}
                                    <div style={{ marginTop: '8px' }}>
                                      <button type="button" onClick={() => setBacsWarnOpen(null)} style={{ fontSize: '0.75rem', color: '#888', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Close</button>
                                    </div>
                                  </div>
                                )}
                              </span>
                            )}
                            {e.bacs_actual_amount != null && e.bacs_ref_total != null &&
                             Math.abs(e.bacs_actual_amount - e.bacs_ref_total) <= 0.005 && (
                              <div style={{ fontSize: '0.78rem', color: '#2e7d32', marginTop: '2px' }} title="Confirmed by financial secretary">
                                ✓ Confirmed
                              </div>
                            )}
                          </div>
                        )}
                        {e.bac_reference && !hasSharedRefTotal(e, entries) &&
                         e.bacs_actual_amount != null && e.bacs_amount != null &&
                         Math.abs(e.bacs_actual_amount - e.bacs_amount) <= 0.005 && (
                          <div style={{ fontSize: '0.78rem', color: '#2e7d32', marginTop: '2px' }} title="Confirmed by financial secretary">
                            ✓ Confirmed
                          </div>
                        )}
                        {e.bac_reference && !hasSharedRefTotal(e, entries) &&
                         e.bacs_actual_amount != null && e.bacs_amount != null &&
                         Math.abs(e.bacs_actual_amount - e.bacs_amount) > 0.005 && (
                          <span style={{ position: 'relative', display: 'inline-block' }}>
                            <button
                              type="button"
                              onClick={() => setBacsWarnOpen(bacsWarnOpen === e.race_number ? null : e.race_number)}
                              style={{ background: 'none', border: 'none', padding: 0, color: 'orange', cursor: 'pointer', fontSize: '1em', marginTop: '2px' }}
                              title="Click for details"
                            >⚠</button>
                            {bacsWarnOpen === e.race_number && (
                              <div style={{ position: 'absolute', zIndex: 200, background: '#fff', border: '1px solid #bbb', borderRadius: '4px', padding: '10px 14px', minWidth: '230px', boxShadow: '0 2px 8px rgba(0,0,0,0.18)', top: '1.6em', left: 0, fontSize: '0.82rem', color: '#333', lineHeight: 1.6 }}>
                                <div><strong>Stated:</strong> £{Number(e.bacs_amount).toFixed(2)}</div>
                                <div><strong>Actual received:</strong> £{Number(e.bacs_actual_amount).toFixed(2)}</div>
                                <button
                                  type="button"
                                  onClick={async () => { await handlePaymentUpdate(e, 'bacs_amount', e.bacs_actual_amount); setBacsWarnOpen(null); }}
                                  style={{ marginTop: '6px', fontSize: '0.82rem', padding: '3px 10px', cursor: 'pointer' }}
                                >Update stated to £{Number(e.bacs_actual_amount).toFixed(2)}</button>
                                <div style={{ marginTop: '8px' }}>
                                  <button type="button" onClick={() => setBacsWarnOpen(null)} style={{ fontSize: '0.75rem', color: '#888', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Close</button>
                                </div>
                              </div>
                            )}
                          </span>
                        )}
                      </td>
                    )}
                    {isFullSec && (
                      <td>
                        {e.submission_refund_status === 'sent' && e.submission_refund_amount != null
                          ? <span style={{ color: 'var(--danger-color)' }}>-{fmtCurrency(e.submission_refund_amount)}</span>
                          : '—'}
                      </td>
                    )}
                    {isFullSec && (
                      <td>{(() => {
                        if (!pricesSet) return fmtAdjustment(null, null);
                        const inSharedRef = e.bac_reference && refSeqMap[e.bac_reference] != null;
                        if (inSharedRef && e.bacs_ref_total != null) {
                          if (refSeqMap[e.bac_reference][e.race_number] === 1) {
                            const refunded = refRefundMap[e.bac_reference] || 0;
                            return fmtAdjustment(+(refFeeMap[e.bac_reference] - e.bacs_ref_total + refunded).toFixed(2), refFeeMap[e.bac_reference]);
                          }
                          return '—';
                        }
                        const entryFee = computeEntryFee(e, entries, prices);
                        const refunded = e.submission_refund_status === 'sent' && e.submission_refund_amount != null ? e.submission_refund_amount : 0;
                        return fmtAdjustment((entryFee || 0) - (e.cheque_amount || 0) - (e.bacs_amount || 0) + refunded, entryFee);
                      })()}</td>
                    )}
                  </tr>
                ))}
              </tbody>
              {pricesSet && (
                <tfoot>
                  <tr>
                    <td colSpan={3}><strong>Total owed</strong></td>
                    <td><strong>{fmtCurrency(totalOwed)}</strong></td>
                    <td colSpan={16} />
                  </tr>
                  {(totalLicence > 0 || totalMembership > 0) && (
                    <>
                      <tr>
                        <td colSpan={3} style={{ paddingLeft: '1.5rem' }}>Entry fees</td>
                        <td>{fmtCurrency(totalOwed - totalLicence - totalMembership)}</td>
                        <td colSpan={16} />
                      </tr>
                      {totalLicence > 0 && (
                        <tr>
                          <td colSpan={3} style={{ paddingLeft: '1.5rem' }}>Day licence / Give It A Go</td>
                          <td>{fmtCurrency(totalLicence)}</td>
                          <td colSpan={16} />
                        </tr>
                      )}
                      {totalMembership > 0 && (
                        <tr>
                          <td colSpan={3} style={{ paddingLeft: '1.5rem' }}>Day membership</td>
                          <td>{fmtCurrency(totalMembership)}</td>
                          <td colSpan={16} />
                        </tr>
                      )}
                    </>
                  )}
                  <tr>
                    <td colSpan={3}>Cheque/PP/Cash received</td>
                    <td>{fmtCurrency(totalCheque)}</td>
                    <td colSpan={16} />
                  </tr>
                  <tr>
                    <td colSpan={3}>BACS confirmed</td>
                    <td>{fmtCurrency(totalBacsConfirmed)}</td>
                    <td colSpan={16} />
                  </tr>
                  {totalRefunded > 0 && (
                    <tr>
                      <td colSpan={3}>Refunded</td>
                      <td style={{ color: 'var(--danger-color)' }}>-{fmtCurrency(totalRefunded)}</td>
                      <td colSpan={16} />
                    </tr>
                  )}
                  <tr>
                    <td colSpan={3}>{totalOutstanding < -0.005 ? 'Overpaid' : 'Outstanding'}</td>
                    <td style={{ color: totalOutstanding > 0.005 ? '#c62828' : '#2e7d32', fontWeight: 600 }}>
                      {fmtCurrency(Math.abs(totalOutstanding))}
                    </td>
                    <td colSpan={16} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          </>
        )}
      </div>

      {/* Copy from another event */}
      {showCopyModal && (
        <CopyFromModal
          event={event}
          sessionToken={sessionToken}
          onCopied={() => loadEntries(true)}
          onClose={() => setShowCopyModal(false)}
        />
      )}

      {/* Add / edit modal */}
      {showForm && (
        <EntryForm
          event={event}
          entry={editing}
          existingNumbers={existingNumbers}
          sessionToken={sessionToken}
          role={role}
          onSaved={() => { setShowForm(false); loadEntries(true); }}
          onClose={() => setShowForm(false)}
        />
      )}

      {/* Decide dialog for a "Pending"/"Accepted" entry, reused from New Entries Received */}
      {decidingEntry && decidingSubmission && (
        <SubmissionActionDialog
          submission={decidingSubmission}
          event={event}
          sessionToken={sessionToken}
          initialAction={decidingAction}
          onClose={() => { setDecidingEntry(null); setDecidingSubmission(null); setDecidingAction(null); }}
          onActioned={() => { setDecidingEntry(null); setDecidingSubmission(null); setDecidingAction(null); loadEntries(true); }}
        />
      )}

      {printingSubmissionId && (
        <SubmissionPrintView
          calendarId={event.CalendarID}
          submissionId={printingSubmissionId}
          sessionToken={sessionToken}
          onClose={() => setPrintingSubmissionId(null)}
        />
      )}

      {requestingRefundFor && (
        <RefundRequestForm
          entry={requestingRefundFor}
          event={event}
          sessionToken={sessionToken}
          onClose={() => setRequestingRefundFor(null)}
          onSubmitted={() => loadEntries(true)}
        />
      )}

      {/* Publish confirm: entries not yet accepted, and/or a restricted event */}
      {publishCheck && createPortal(
        <div className="confirm-overlay">
          <div className="confirm-card">
            <h3>⚠️ Publish Entry List</h3>
            {publishCheck.received > 0 && (
              <p>
                {publishCheck.received} online {publishCheck.received === 1 ? 'entry is' : 'entries are'} still
                {' '}<strong>Received</strong> (not yet reviewed). {publishCheck.received === 1 ? 'It' : 'They'} won&apos;t
                be on the public list until you hold or accept {publishCheck.received === 1 ? 'it' : 'them'}.
              </p>
            )}
            {publishCheck.pending > 0 && (
              <p>
                {publishCheck.pending} {publishCheck.pending === 1 ? 'entry is' : 'entries are'}
                {' '}<strong>Held for review</strong>. Held entries won&apos;t be on the public list (or sent to
                timing) until you accept them.
              </p>
            )}
            {publishCheck.restricted && (
              <p>
                This event can sometimes be oversubscribed and we may need to turn people away
                after they have put their entries in.
              </p>
            )}
            <p>Are you sure you want to go ahead? It&apos;s best to wait until the full entry list is confirmed.</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setPublishCheck(null)}>Cancel</button>
              <button
                className="btn-confirm-delete"
                onClick={() => {
                  setPublishCheck(null);
                  handlePublish(true);
                }}
              >
                Yes, Publish
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete confirm */}
      {deleting && createPortal(
        <div className="confirm-overlay">
          <div className="confirm-card">
            <h3>⚠️ Delete Entry</h3>
            <p>
              Remove <strong>#{deleting.race_number} – {deleting.rider_first} {deleting.rider_last}</strong> from this event?
              This cannot be undone.
            </p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setDeleting(null)}>Cancel</button>
              <button className="btn-confirm-delete" onClick={handleDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

function BoolCell({ v }) {
  return v
    ? <span className="bool-tick">✓</span>
    : <span className="bool-cross">–</span>;
}

const FLAG_CSS = { class_rl: 'flag-rl', class_vintage: 'flag-vintage', class_classic: 'flag-classic', class_fe: 'flag-fe' };

export const CURRENT_YEAR = new Date().getFullYear();

/** Tri-state of a membership verification field pair: 'true' (verified — dateVal
 *  is set), 'false' (explicitly marked not valid — okVal===false), or null
 *  (not yet checked — the default). */
function triState(dateVal, okVal) {
  if (dateVal) return 'true';
  if (okVal === false) return 'false';
  return null;
}

/** Click/tap cycle order for the editable cell: ? -> ✓ -> ✗ -> ? */
function nextTriState(current) {
  if (current === 'true') return 'false';
  if (current === 'false') return null;
  return 'true';
}

const TRISTATE_CSS = {
  tick:    { bool: 'bool-tick',    flag: 'flag-on' },
  stale:   { bool: 'bool-stale',   flag: 'flag-stale' },
  false:   { bool: 'bool-false',   flag: 'flag-false' },
  unknown: { bool: 'bool-unknown', flag: 'flag-unknown' },
};

/** Tri-state flag cell for membership verification fields: true=tick (date
 *  set, shown as a tooltip; warns if it's from a prior year), false=cross
 *  (explicitly marked not valid), null=question mark (not yet checked). */
function TriStateFlagCell({ dateVal, okVal, editable, onToggle }) {
  const state    = triState(dateVal, okVal);
  const isStale  = state === 'true' && new Date(dateVal).getFullYear() < CURRENT_YEAR;
  const cssKey   = state === 'true' ? (isStale ? 'stale' : 'tick') : state === 'false' ? 'false' : 'unknown';
  const label    = cssKey === 'tick' ? '✓' : cssKey === 'stale' ? '⚠' : cssKey === 'false' ? '✗' : '?';
  const tip      = state === 'true'
    ? `Verified ${dateVal}${isStale ? ' – previous year' : ''}${editable ? ' – click to cycle' : ''}`
    : state === 'false'
      ? `Marked not valid${editable ? ' – click to cycle' : ''}`
      : editable ? 'Not yet checked – click to mark verified' : 'Not yet checked';

  if (!editable) {
    return <span className={TRISTATE_CSS[cssKey].bool} title={tip}>{label}</span>;
  }
  return (
    <button className={`flag-toggle ${TRISTATE_CSS[cssKey].flag}`} onClick={onToggle} title={tip}>
      {label}
    </button>
  );
}

/** Like BoolCell but clickable when editable=true */
function FlagCell({ v, editable, onToggle, flagKey }) {
  const subCls = FLAG_CSS[flagKey] || '';
  if (!editable) return <BoolCell v={v} />;
  return (
    <button
      className={`flag-toggle ${subCls} ${v ? 'flag-on' : 'flag-off'}`}
      onClick={onToggle}
      title="Click to toggle"
    >
      {v ? '✓' : '–'}
    </button>
  );
}

/** Note cell — shows first 20 chars of note (or + if empty). Full note on hover. Click opens modal to edit. */
// Notes are saved on entry-list rows, which an online entry only gets once
// the event secretary holds or accepts it. The financial view also lists
// entries still 'received' (submission_status), so those show this instead
// of a note box that couldn't save.
function NoNoteYet() {
  return (
    <span style={{ color: '#aaa' }}
          title="A note can be added once the event secretary has held or accepted this entry">—</span>
  );
}

function NoteCell({ value, label, onSave }) {
  const [open, setOpen]     = useState(false);
  const [draft, setDraft]   = useState(value || '');
  const overlayClick        = useRef(false);

  const openModal  = () => { setDraft(value || ''); setOpen(true); };
  const closeModal = () => setOpen(false);
  const save       = () => { onSave(draft); setOpen(false); };

  const preview = value ? (value.length > 20 ? value.slice(0, 20) + '…' : value) : null;

  return (
    <>
      <span
        onClick={openModal}
        title={value || 'Add an optional note, this will be visible to other secretaries'}
        style={{ cursor: 'pointer' }}
      >
        {preview
          ? <span style={{ fontStyle: 'italic', fontSize: '0.82rem', color: '#555' }}>{preview}</span>
          : <span style={{ color: '#aaa' }}>+</span>
        }
      </span>
      {open && createPortal(
        <div
          className="confirm-overlay"
          onMouseDown={e => { overlayClick.current = e.target === e.currentTarget; }}
          onClick={() => { if (overlayClick.current) closeModal(); }}
        >
          <div className="confirm-card" style={{ maxWidth: '28rem', width: '90vw' }}>
            <h3>Note{label ? ` — ${label}` : ''}</h3>
            <textarea
              autoFocus
              value={draft}
              onChange={e => setDraft(e.target.value)}
              rows={4}
              placeholder="Add an optional note, this will be visible to other secretaries"
              style={{ width: '100%', maxWidth: '100%', fontSize: '0.95rem', resize: 'both', marginTop: '0.75rem', boxSizing: 'border-box', padding: '0.4rem 0.5rem' }}
            />
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={closeModal}>Cancel</button>
              <button className="btn-save" onClick={save}>Save</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Entry form (add / edit) with race-number search ─────────────────────────

function EntryForm({ event, entry, existingNumbers, sessionToken, role, onSaved, onClose }) {
  const isEdit = Boolean(entry);
  const isFullSec = role === 'secretary' || role === 'event-super-sec';

  const [form, setForm]         = useState(entry ? { ...entry } : blankEntry());
  const [, setSearching] = useState(false);
  const [history, setHistory]     = useState(null);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [dupWarning, setDupWarning] = useState(null);
  const debounceRef = useRef(null);

  const doSearch = async (num) => {
    setSearching(true); setHistory(null);
    try {
      // event_date scopes the backend's "same year" verification check to
      // THIS event's year, not today's real-world date — otherwise entering
      // entries for a future/past season ahead of/behind the calendar shows
      // a stale tick left over from an unrelated same-real-world-year event.
      const qs = event?.Date ? `?event_date=${encodeURIComponent(event.Date.substring(0, 10))}` : '';
      const results = await api(`/api/secretary/search-number/${num}${qs}`, {}, sessionToken);
      setHistory(results);
    } catch (err) { setError(err.message); }
    finally { setSearching(false); }
  };

  // Fix 2: auto-search when race_number changes, debounced 500ms
  useEffect(() => {
    if (isEdit) return;
    const num = parseInt(form.race_number, 10);
    if (!num) { setHistory(null); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(num), 500);
    return () => clearTimeout(debounceRef.current);
  }, [form.race_number]);

  const prefill = (h) => {
    // Only carry membership dates forward if verified in the SAME YEAR AS
    // THIS EVENT (not today's real-world year — see doSearch above).
    const eventYear = event?.Date ? new Date(`${event.Date.substring(0, 10)}T12:00:00`).getFullYear() : CURRENT_YEAR;
    const sameYear = (d) => d && new Date(d).getFullYear() === eventYear;
    setForm({
      race_number: h.race_number,
      rider_first:      h.rider_first      || '',
      rider_last:       h.rider_last       || '',
      passenger_first:  h.passenger_first  || '',
      passenger_last:   h.passenger_last   || '',
      type:             h.type             || 'Motorcycle',
      manufacturer:     h.manufacturer     || '',
      model:            h.model            || '',
      cc:               h.cc               || '',
      class_rl:         h.class_rl         || false,
      class_vintage:    h.class_vintage    || false,
      class_classic:    h.class_classic    || false,
      class_fe:         h.class_fe         || false,
      acu_licence_verified:     sameYear(h.acu_licence_verified)     ? h.acu_licence_verified     : null,
      nhca_membership_verified: sameYear(h.nhca_membership_verified) ? h.nhca_membership_verified : null,
    });
    setHistory(null);
  };

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [key]: val }));
  };

  const doSave = async () => {
    setSaving(true); setError(null); setDupWarning(null);
    try {
      const payload = {
        ...form,
        race_number: parseInt(form.race_number, 10),
        cc: form.cc ? parseInt(form.cc, 10) : null,
        cheque_amount: form.cheque_amount !== '' && form.cheque_amount != null
          ? parseFloat(form.cheque_amount)
          : null,
      };
      if (isEdit) {
        await api(
          `/api/secretary/entries/${event.CalendarID}/${entry.race_number}`,
          { method: 'PUT', body: JSON.stringify(payload) },
          sessionToken
        );
      } else {
        await api(
          `/api/secretary/entries/${event.CalendarID}`,
          { method: 'POST', body: JSON.stringify(payload) },
          sessionToken
        );
      }
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const handleSave = () => {
    const missing = [];
    if (!form.race_number) missing.push('Race number');
    if (!form.rider_first) missing.push('Rider first name');
    if (!form.rider_last)  missing.push('Rider last name');
    if (!form.type)        missing.push('Type');
    if (!form.cc)          missing.push('Engine CC');
    if (missing.length > 0) {
      setError('Required fields missing: ' + missing.join(', '));
      return;
    }
    const num = parseInt(form.race_number, 10);
    if (!isEdit && existingNumbers.has(num)) { setDupWarning(num); return; }
    // An edit can't overwrite another entry: changing to a number already in
    // the list is refused (the backend refuses it too, with 409).
    if (isEdit && num !== entry.race_number && existingNumbers.has(num)) {
      setError(`Race number #${num} is already in this entry list. Change that entry's number first, or pick another.`);
      return;
    }
    doSave();
  };

  return (
    <div className="sec-overlay">
      <div className="sec-modal">
        <div className="sec-modal-header">
          <h3>{isEdit ? `Edit Entry #${entry.race_number}` : 'Add Entry'}</h3>
          <button className="sec-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="sec-modal-body">

          {/* Race number search (only for new entries) */}
          {!isEdit && (
            <div className="num-search-panel">
              <h4>Race Number</h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '0.75rem' }}>
                Enter the race number — previous entries will appear automatically.
              </p>
              <div className="form-group" style={{ maxWidth: '160px' }}>
                <input
                  className="num-search-input"
                  type="number"
                  min="1"
                  placeholder="e.g. 42"
                  value={form.race_number}
                  onChange={set('race_number')}
                  autoFocus
                />
              </div>

              {/* History results */}
              {history !== null && (
                <div className="history-results">
                  {history.length === 0 ? (
                    <p className="history-none">
                      No previous entries for this number — fill in the details below.
                    </p>
                  ) : (
                    <>
                      <p>
                        {history.length} previous entry/entries found — click to pre-fill:
                      </p>
                      {history.map((h, i) => (
                        <div
                          key={i}
                          className="history-card"
                          onClick={() => prefill(h)}
                        >
                          <div className="history-card-name">
                            {[h.rider_first, h.rider_last].filter(Boolean).join(' ') || 'Unknown Rider'}
                          </div>
                          <div className="history-card-bike">
                            {[h.manufacturer, h.model, h.cc ? `${h.cc}cc` : null]
                              .filter(Boolean).join(' ')} · last seen {fmtDate(h.event_date)}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {error && (
            <div className="error-message" style={{ marginBottom: '1rem' }}>
              <p>⚠️ {error}</p>
              <button onClick={() => setError(null)}>Dismiss</button>
            </div>
          )}

          {/* Form fields */}
          <div className="form-grid">

            {/* Race number (on an edit, only the main secretary can change it) */}
            {(!isEdit || isFullSec) && (
              <div className="form-group">
                <label>Race Number *</label>
                <input
                  type="number"
                  min="1"
                  value={form.race_number}
                  onChange={set('race_number')}
                  placeholder="e.g. 42"
                />
              </div>
            )}

            {/* Rider */}
            <p className="form-section-label">Rider</p>
            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input type="text" value={form.rider_first} onChange={set('rider_first')} />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input type="text" value={form.rider_last} onChange={set('rider_last')} />
              </div>
            </div>

            {/* Passenger */}
            <p className="form-section-label" style={{ marginTop: '0.25rem' }}>Passenger (optional)</p>
            <div className="form-row">
              <div className="form-group">
                <label>First Name</label>
                <input type="text" value={form.passenger_first} onChange={set('passenger_first')} />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input type="text" value={form.passenger_last} onChange={set('passenger_last')} />
              </div>
            </div>

            <hr className="form-divider" />

            {/* Machine */}
            <p className="form-section-label">Machine</p>
            <div className="form-group">
              <label>Type</label>
              <select value={form.type} onChange={set('type')} style={{ fontSize: '1rem' }}>
                <option>Motorcycle</option>
                <option>Sidecar</option>
                <option>Trike</option>
                <option>Morgan1P</option>
                <option>Morgan2P</option>
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Manufacturer</label>
                <input type="text" value={form.manufacturer} onChange={set('manufacturer')} placeholder="e.g. Honda" />
              </div>
              <div className="form-group">
                <label>Model</label>
                <input type="text" value={form.model} onChange={set('model')} placeholder="e.g. CBR600" />
              </div>
            </div>
            <div className="form-group" style={{ maxWidth: '140px' }}>
              <label>Engine CC</label>
              <input type="number" min="1" value={form.cc} onChange={set('cc')} placeholder="e.g. 600" />
            </div>

            <hr className="form-divider" />

            {/* Classes */}
            <p className="form-section-label">Classes Entered</p>
            <div className="checkbox-group">
              {[
                ['class_rl',      'Road Legal'],
                ['class_vintage', 'Vintage'],
                ['class_classic', 'Classic'],
                ['class_fe',      'Forgotten Era'],
              ].map(([key, label]) => (
                <label key={key} className="check-pill">
                  <input
                    type="checkbox"
                    checked={form[key]}
                    onChange={set(key)}
                  />
                  {form[key] ? '✓ ' : ''}{label}
                </label>
              ))}
            </div>

            <hr className="form-divider" />

            {/* Note */}
            <div className="form-group">
              <label>Note</label>
              <textarea
                rows={3}
                value={form.note || ''}
                onChange={set('note')}
                placeholder="Add an optional note, this will be visible to other secretaries"
                style={{ resize: 'vertical', fontSize: '0.95rem' }}
              />
            </div>

            {/* Payment — full secretary only */}
            {isFullSec && (
              <>
                <hr className="form-divider" />
                <p className="form-section-label">Payment</p>
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      Cheque / PP / Cash
                      <InfoTip text="Enter the cheque, PayPal or cash payment received. If the payment covers two days (e.g. Sat/Sun) enter half of the amount for each day." />
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.cheque_amount ?? ''}
                      onChange={set('cheque_amount')}
                      placeholder="e.g. 35.00"
                    />
                  </div>
                  <div className="form-group">
                    <label>
                      Payment Ref
                      <InfoTip text="Input the bank reference given by the entrant on their entry form." />
                    </label>
                    <input
                      type="text"
                      value={form.bac_reference || ''}
                      onChange={set('bac_reference')}
                      placeholder="e.g. SMITH042"
                    />
                  </div>
                </div>
              </>
            )}

          </div>
        </div>

        <div className="sec-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Entry'}
          </button>
        </div>
      </div>

      {dupWarning && (
        <div className="confirm-overlay">
          <div className="confirm-card">
            <h3>⚠️ Duplicate Race Number</h3>
            <p>Race number <strong>#{dupWarning}</strong> is already in this entry list. Do you want to overwrite the existing entry?</p>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setDupWarning(null)}>Cancel</button>
              <button className="btn-confirm-delete" onClick={doSave}>Yes, Overwrite</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Copy-from-event modal ────────────────────────────────────────────────────

function CopyFromModal({ event, sessionToken, onCopied, onClose }) {
  const [events, setEvents]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [copying, setCopying]       = useState(false);
  const [error, setError]           = useState(null);
  const [copied, setCopied]         = useState(null);

  useEffect(() => {
    api(`/api/secretary/events?year=${event.Date.slice(0, 4)}`, {}, sessionToken)
      .then(evs => {
        const others = evs.filter(ev => ev.CalendarID !== event.CalendarID);
        setEvents(others);
        const eventDate = new Date(event.Date.split('T')[0] + 'T00:00:00');
        const adjacent = others.find(ev => {
          const d = new Date(ev.Date.split('T')[0] + 'T00:00:00');
          return Math.round(Math.abs(d - eventDate) / 86400000) === 1;
        });
        const defaultId = adjacent
          ? adjacent.CalendarID
          : others.length > 0 ? others[0].CalendarID : null;
        setSelectedId(defaultId);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const isAdjacent = (ev) => {
    const eventDate = new Date(event.Date.split('T')[0] + 'T00:00:00');
    const d = new Date(ev.Date.split('T')[0] + 'T00:00:00');
    return Math.round(Math.abs(d - eventDate) / 86400000) === 1;
  };

  const selectedEvent = events.find(ev => ev.CalendarID === selectedId);

  const handleCopy = async () => {
    if (!selectedId) return;
    setCopying(true);
    setError(null);
    try {
      const res = await api(
        `/api/secretary/entries/${event.CalendarID}/copy-from`,
        { method: 'POST', body: JSON.stringify({ source_calendar_id: selectedId }) },
        sessionToken
      );
      setCopied(res.copied);
      onCopied();
    } catch (err) {
      setError(err.message);
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="sec-overlay">
      <div className="sec-modal" style={{ maxWidth: '480px' }}>
        <div className="sec-modal-header">
          <h3>Copy Entries from Another Event</h3>
          <button className="sec-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="sec-modal-body">
          {copied != null ? (
            <div style={{ textAlign: 'center', padding: '1rem 0' }}>
              <p style={{ fontSize: '1.1rem', fontWeight: 600, color: '#2e7d32' }}>
                ✅ {copied} {copied === 1 ? 'entry' : 'entries'} copied
              </p>
              <p style={{ color: '#555', marginTop: '0.5rem', fontSize: '0.9rem' }}>
                Where a race number already existed, rider details were updated but existing notes and verification dates were kept.
              </p>
            </div>
          ) : loading ? (
            <Spinner />
          ) : (
            <>
              <p style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#555' }}>
                Copies all entries from the selected event into{' '}
                <strong>{event.eventname || `${event.Hill} – ${fmtDate(event.Date)}`}</strong>.
                Where the same race number already exists here, rider details will be updated but existing notes and verification dates are preserved.
              </p>
              {error && (
                <div className="error-message" style={{ marginBottom: '0.75rem' }}>
                  <p>⚠️ {error}</p>
                  <button onClick={() => setError(null)}>Dismiss</button>
                </div>
              )}
              {events.length === 0 ? (
                <p style={{ color: '#888' }}>No other events are available to copy from.</p>
              ) : (
                <div className="form-group">
                  <label style={{ fontWeight: 600 }}>Copy from:</label>
                  <select
                    value={selectedId ?? ''}
                    onChange={e => setSelectedId(Number(e.target.value))}
                    style={{ width: '100%', fontSize: '1rem', padding: '0.4rem 0.6rem', marginTop: '0.4rem' }}
                  >
                    {events.map(ev => (
                      <option key={ev.CalendarID} value={ev.CalendarID}>
                        {isAdjacent(ev) ? '★ ' : ''}{ev.Hill} – {fmtDate(ev.Date)}{isAdjacent(ev) ? ' (adjacent day)' : ''}
                      </option>
                    ))}
                  </select>
                  {selectedEvent && isAdjacent(selectedEvent) && (
                    <p style={{ fontSize: '0.82rem', color: '#1b3d6d', marginTop: '0.4rem' }}>
                      ★ This event is one day before/after the current event — likely the right one for a two-day event.
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="sec-modal-footer">
          {copied != null ? (
            <button className="btn-save" onClick={onClose}>Done</button>
          ) : (
            <>
              <button className="btn-cancel" onClick={onClose}>Cancel</button>
              <button
                className="btn-save"
                onClick={handleCopy}
                disabled={copying || !selectedId || events.length === 0}
              >
                {copying ? 'Copying…' : 'Copy Entries'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Inline editable cell ────────────────────────────────────────────────────

export function InlineEditCell({ value, inputType = 'text', prefix = '', placeholder = '', onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');

  const open = () => {
    setDraft(value != null ? String(value) : '');
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    let parsed;
    if (inputType === 'number') {
      parsed = draft.trim() === '' ? null : parseFloat(draft);
      if (isNaN(parsed)) parsed = null;
    } else {
      parsed = draft.trim() || null;
    }
    onSave(parsed);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type={inputType}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        style={{ width: inputType === 'number' ? '75px' : '100px', fontSize: '0.85rem', padding: '1px 3px' }}
        step={inputType === 'number' ? '0.01' : undefined}
        min={inputType === 'number' ? '0' : undefined}
        placeholder={placeholder}
      />
    );
  }

  return (
    <span
      onClick={open}
      title="Click to edit"
      style={{
        cursor: 'pointer',
        borderBottom: '1px dashed var(--text-light)',
        color: value != null ? 'inherit' : '#aaa',
        minWidth: '2rem',
        display: 'inline-block',
      }}
    >
      {value != null
        ? (prefix + (inputType === 'number' ? Number(value).toFixed(2) : value))
        : '—'}
    </span>
  );
}

// A row's whole fee: its 1st/2nd-machine tier fee, plus the rider's day
// licence and day membership on the row that carries them
// (entry_list.licence_fee / membership_fee, first row only). Give It A Go
// replaces the entry fee with its all-in price.
// Mirrors entry_fees.row_fee in the backend.
function computeEntryFee(entry, allEntries, prices) {
  if (entry.licence_option === 'giag' && entry.licence_fee != null) return entry.licence_fee;
  const fee = machineTierFee(entry, allEntries, prices);
  if (fee == null) return fee;
  return +(fee + (entry.licence_fee || 0) + (entry.membership_fee || 0)).toFixed(2);
}

const LICENCE_LABELS = { day_licence: 'Day', giag: 'GIAG' };

// The Licence column: "Day", "GIAG", "Memb" (a day membership) or "Day + Memb".
function licenceLabel(e) {
  return [LICENCE_LABELS[e.licence_option], e.day_membership && 'Memb'].filter(Boolean).join(' + ') || '—';
}

function machineTierFee(entry, allEntries, prices) {
  if (!prices) return null;
  const is3w = t => ['sidecar', 'trike', 'morgan2p'].includes((t || '').toLowerCase().trim());
  const riderEntries = allEntries
    .filter(e =>
      (e.rider_first || '').toLowerCase() === (entry.rider_first || '').toLowerCase() &&
      (e.rider_last || '').toLowerCase() === (entry.rider_last || '').toLowerCase()
    )
    .sort((a, b) => {
      if (a.entry_sequence == null && b.entry_sequence == null) return a.race_number - b.race_number;
      if (a.entry_sequence == null) return 1;
      if (b.entry_sequence == null) return -1;
      return a.entry_sequence - b.entry_sequence;
    });
  let bike_n = 0, w3_n = 0;
  for (const e of riderEntries) {
    if (is3w(e.type)) w3_n++; else bike_n++;
    if (e.race_number === entry.race_number) {
      if (is3w(e.type)) {
        return w3_n === 1 ? prices.first_3w : prices.second_3w ?? null;
      } else {
        return bike_n === 1 ? prices.first_bike : prices.second_bike ?? null;
      }
    }
  }
  return null;
}

// True when this row's BACS payment is part of a shared-total group — either
// because another live entry_list row already shares its bac_reference (the
// manual-linking flow), or because bacs_ref_total was already stamped with
// the group's full stated total by the coverage-code accept path even though
// no other row exists yet (e.g. the payer's own entry got accepted before
// the covered rider's). A trivial solo BACS payment has bacs_ref_total ==
// bacs_amount (both just its own fee), so that case is excluded.
function hasSharedRefTotal(entry, allEntries) {
  if (!entry.bac_reference) return false;
  if (allEntries.some(o => o.race_number !== entry.race_number && o.bac_reference === entry.bac_reference)) {
    return true;
  }
  return entry.bacs_ref_total != null &&
    Math.abs(entry.bacs_ref_total - (entry.bacs_amount ?? 0)) > 0.005;
}

export function fmtAdjustment(adj, fee) {
  if (fee == null || adj == null) return '—';
  if (Math.abs(adj) < 0.005) return <span style={{ color: '#2e7d32', fontWeight: 600 }}>✓</span>;
  if (adj > 0) return <span style={{ color: '#c62828' }}>{'–' + fmtCurrency(adj)}</span>;
  return <span style={{ color: '#2e7d32' }}>{'+' + fmtCurrency(-adj)}</span>;
}

// ─── Financial entry list ────────────────────────────────────────────────────

function formatMachines(machines) {
  const counts = {};
  machines.forEach(m => {
    const t = m.type || 'Unknown';
    counts[t] = (counts[t] || 0) + 1;
  });
  return Object.entries(counts).map(([t, c]) => c > 1 ? `${t} ×${c}` : t).join(', ');
}

export function fmtCurrency(val) {
  if (val == null) return '—';
  return `£${Number(val).toFixed(2)}`;
}

// Groups a financial-view response's riders[] by bac_reference, summing
// fees/amounts across every machine that shares it. Pulled out of
// FinancialEntryList so FinancialDashboard can reuse the identical logic
// across many events without duplicating it.
export function computeBacsGroups(riders) {
  const bacsGroupsMap = {};
  for (const rider of riders) {
    for (const m of rider.machines) {
      if (m.bac_reference) {
        if (!bacsGroupsMap[m.bac_reference]) {
          bacsGroupsMap[m.bac_reference] = { bac_reference: m.bac_reference, bacs_ref_total: m.bacs_ref_total, bacs_actual_amount: m.bacs_actual_amount, bacs_amount_total: 0, entries: [], fees_total: 0 };
        }
        bacsGroupsMap[m.bac_reference].entries.push({
          rider_first: rider.rider_first, rider_last: rider.rider_last, race_number: m.race_number,
          submission_id: m.submission_id, type: m.type, rider_email: m.rider_email, payment_method: m.payment_method,
          note: m.note, submission_status: m.submission_status,
        });
        if (m.fee != null) bacsGroupsMap[m.bac_reference].fees_total = +((bacsGroupsMap[m.bac_reference].fees_total + m.fee)).toFixed(2);
        if (m.bacs_amount != null) bacsGroupsMap[m.bac_reference].bacs_amount_total = +((bacsGroupsMap[m.bac_reference].bacs_amount_total + m.bacs_amount)).toFixed(2);
      }
    }
  }
  return Object.values(bacsGroupsMap).sort((a, b) => a.bac_reference.localeCompare(b.bac_reference));
}

// Detail modal for a single entry clicked from the BACS Reconciliation
// table's "Entries" cell. rider_email/payment_method only exist for
// entries with a submission_id (i.e. came in via the online entry form) —
// a manually-added entry shows a fallback instead. The Actual Received
// figure is shared across the whole bac_reference group (see
// handleActualUpdate), so editing it here saves for every entry in the
// group, same as the inline edit in the main reconciliation row.
function BacsEntryDetailModal({ entry, group, onSaveActual, onSaveNote, onClose }) {
  const riderName = [entry.rider_first, entry.rider_last].filter(Boolean).join(' ') || '—';
  return (
    <div className="sec-overlay">
      <div className="sec-modal" style={{ maxWidth: '420px' }}>
        <div className="sec-modal-header">
          <h3>#{entry.race_number} {riderName}</h3>
          <button className="sec-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="sec-modal-body">
          <div className="form-group">
            <label>Rider</label>
            <div>{riderName}</div>
          </div>
          <div className="form-group">
            <label>Machine</label>
            <div>{entry.type || '—'}</div>
          </div>
          <div className="form-group">
            <label>Rider email</label>
            <div>{entry.rider_email || (entry.submission_id ? '—' : 'Manually entered — no submission on file')}</div>
          </div>
          <div className="form-group">
            <label>Payment method</label>
            <div>{entry.payment_method ? (entry.payment_method === 'covered' ? 'Covered by another rider’s payment' : entry.payment_method.toUpperCase()) : '—'}</div>
          </div>
          <div className="form-group">
            <label>BACS reference</label>
            <div style={{ fontFamily: 'monospace' }}>{group.bac_reference}</div>
          </div>
          <div className="form-group">
            <label>Stated</label>
            <div>{group.bacs_ref_total != null ? fmtCurrency(group.bacs_ref_total) : group.bacs_amount_total > 0 ? fmtCurrency(group.bacs_amount_total) : '—'}</div>
          </div>
          <div className="form-group">
            <label>Actual received</label>
            <InlineEditCell
              value={group.bacs_actual_amount}
              inputType="number"
              prefix="£"
              placeholder="0.00"
              onSave={onSaveActual}
            />
            {group.entries.length > 1 && (
              <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '2px' }}>
                Applies to all {group.entries.length} entries sharing reference {group.bac_reference}.
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Note</label>
            {entry.submission_status === 'received'
              ? <NoNoteYet />
              : <NoteCell value={entry.note} label={riderName} onSave={onSaveNote} />}
          </div>
        </div>
        <div className="sec-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function FinancialEntryList({ event, sessionToken, onBack, backLabel = '← Back to Events' }) {
  const eventYear = event.Date ? new Date(event.Date.split('T')[0] + 'T00:00:00').getFullYear() : null;
  const showRefundRequests = eventYear !== null && eventYear >= ONLINE_ENTRY_FIRST_YEAR;

  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  // Entry clicked from the BACS Reconciliation table's "Entries" cell —
  // opens BacsEntryDetailModal with that entry's info plus its group's
  // stated/actual reconciliation figures.
  const [detailEntry, setDetailEntry] = useState(null);
  // Raise a refund request — the financial secretary is often the one who
  // spots an overpayment first (via the columns below), so this needs its
  // own trigger here rather than only existing on the entry secretary's
  // EntryList screen. Keyed off a machine's submission_id (manually-added
  // entries have none, so the button just doesn't appear for those riders).
  const [requestingRefundFor, setRequestingRefundFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api(`/api/secretary/entries/${event.CalendarID}/financial-view`, {}, sessionToken);
      setData(d);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [event.CalendarID, sessionToken]);

  useEffect(() => { load(); }, [load]);

  const handleNoteUpdate = async (rider, newNote) => {
    try {
      await api(
        `/api/secretary/entries/${event.CalendarID}/rider-note`,
        { method: 'PUT', body: JSON.stringify({ rider_first: rider.rider_first, rider_last: rider.rider_last, note: newNote || null }) },
        sessionToken
      );
      setData(prev => ({
        ...prev,
        riders: prev.riders.map(r =>
          r.rider_first === rider.rider_first && r.rider_last === rider.rider_last
            // Backend broadcasts the note to every entry_list row for this
            // rider (see update_rider_note) — mirror that here so the BACS
            // Reconciliation table/modal (sourced from machines[].note via
            // computeBacsGroups) stay in sync with this same edit.
            ? { ...r, note: newNote || null, machines: r.machines.map(m => ({ ...m, note: newNote || null })) }
            : r
        ),
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleActualUpdate = async (bac_reference, newActual) => {
    try {
      await api(
        `/api/secretary/entries/${event.CalendarID}/bacs-actual-amount`,
        { method: 'PUT', body: JSON.stringify({ bac_reference, bacs_actual_amount: newActual }) },
        sessionToken
      );
      setData(prev => ({
        ...prev,
        riders: prev.riders.map(r => ({
          ...r,
          machines: r.machines.map(m =>
            m.bac_reference === bac_reference ? { ...m, bacs_actual_amount: newActual } : m
          ),
        })),
      }));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleExportCsv = async () => {
    try {
      const res = await fetch(`/api/secretary/entries/${event.CalendarID}/financial-view/export`, {
        headers: { 'X-API-Key': API_KEY, Authorization: `Bearer ${sessionToken}` },
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const a = document.createElement('a');
      a.href = url;
      a.download = match ? match[1] : `entries_${event.CalendarID}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePriceSave = async (field, val) => {
    const current = data?.prices || {};
    const payload = {
      first_bike_price:  current.first_bike  ?? null,
      second_bike_price: current.second_bike ?? null,
      first_3w_price:    current.first_3w    ?? null,
      second_3w_price:   current.second_3w   ?? null,
      [field]: val,
    };
    try {
      await api(
        `/api/secretary/events/${event.CalendarID}/prices`,
        { method: 'PUT', body: JSON.stringify(payload) },
        sessionToken
      );
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) return <Spinner />;

  const { prices = {}, riders = [] } = data || {};
  const pricesSet = prices.first_bike != null || prices.first_3w != null;

  const totalOwed     = riders.reduce((s, r) => s + (r.total_fee || 0), 0);
  // The day licence / Give It A Go and day membership parts of totalOwed,
  // shown as their own sub-totals.
  const totalLicence  = +riders.reduce((s, r) => s + (r.licence_fee || 0), 0).toFixed(2);
  const totalMembership = +riders.reduce((s, r) => s + (r.membership_fee || 0), 0).toFixed(2);
  const totalCheque   = riders.reduce((s, r) => s + (r.cheque_amount || 0), 0);
  const totalRefunded = riders.reduce((s, r) => s + (r.refunded_amount || 0), 0);

  const bacsGroupList    = computeBacsGroups(riders);
  const totalBacsActual  = bacsGroupList.reduce((s, g) => s + (g.bacs_actual_amount || 0), 0);
  const totalReceived    = totalCheque + totalBacsActual;
  const totalOutstanding = totalOwed - totalReceived + totalRefunded;

  return (
    <div className="sec-section">
      <div className="sec-section-header">
        <div>
          <button
            className="btn-edit"
            onClick={onBack}
            style={{ marginBottom: '0.5rem', display: 'inline-block' }}
          >
            {backLabel}
          </button>
          <h2>💰 {event.eventname || `${event.Hill} – ${fmtDate(event.Date)}`}</h2>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <button className="btn-add-entry" onClick={handleExportCsv}>⬇ Download entry data</button>
          <span className="entry-count-badge">
            {riders.length} rider{riders.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div style={{ background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '4px', padding: '0.5rem 1rem', marginBottom: '1rem', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', alignItems: 'center', fontSize: '0.88rem' }}>
        <strong>Entry Fees (£):</strong>
        <span>1st Bike: <InlineEditCell value={prices.first_bike} inputType="number" prefix="£" placeholder="0.00" onSave={v => handlePriceSave('first_bike_price', v)} /></span>
        <span>2nd Bike: <InlineEditCell value={prices.second_bike} inputType="number" prefix="£" placeholder="0.00" onSave={v => handlePriceSave('second_bike_price', v)} /></span>
        <span>1st 3-Wheeler: <InlineEditCell value={prices.first_3w} inputType="number" prefix="£" placeholder="0.00" onSave={v => handlePriceSave('first_3w_price', v)} /></span>
        <span>2nd 3-Wheeler: <InlineEditCell value={prices.second_3w} inputType="number" prefix="£" placeholder="0.00" onSave={v => handlePriceSave('second_3w_price', v)} /></span>
      </div>

      {showRefundRequests && (
        <RefundRequestsList event={event} sessionToken={sessionToken} onEntryListChanged={load} />
      )}

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          <p>⚠️ {error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {riders.length === 0 ? (
        <EmptyState icon="📋" message="No entries yet for this event." />
      ) : (
        <>
          <div className="entries-table-wrap">
            <table className="entries-table">
              <thead>
                <tr>
                  <th>Rider</th>
                  <th>Machines</th>
                  <th>Fee</th>
                  <th title="Day licence or Give It A Go, and Memb: a day NHCA membership (included in Fee)">Licence</th>
                  <th title="Cheque/PayPal/Cash amount from event secretary">Cheque/PP/Cash</th>
                  <th title="BACS amount allocated by event secretary">BACS</th>
                  <th title="Date the online entry form was submitted, if applicable">Submitted</th>
                  <th title="Amount refunded so far (cheque/BACS amounts above are never adjusted down — this is tracked separately)">Refunded</th>
                  <th title="Fee minus all payments received, plus back any amount refunded">Adjustment</th>
                  <th className="note-col">Note</th>
                </tr>
              </thead>
              <tbody>
                {riders.map(r => {
                  const refundableMachine = r.machines.find(m => m.submission_id);
                  return (
                  <tr key={`${r.rider_first}-${r.rider_last}`}>
                    <td>
                      {[r.rider_first, r.rider_last].filter(Boolean).join(' ') || '—'}
                      {refundableMachine && (
                        <button
                          type="button"
                          className="btn-edit"
                          style={{ display: 'block', marginTop: '3px', fontSize: '0.7rem', padding: '0.1rem 0.4rem' }}
                          onClick={() => setRequestingRefundFor({
                            submission_id: refundableMachine.submission_id,
                            race_number: refundableMachine.race_number,
                            rider_first: r.rider_first,
                            rider_last: r.rider_last,
                          })}
                          title="Request a refund for this rider"
                        >
                          Refund…
                        </button>
                      )}
                    </td>
                    <td>{formatMachines(r.machines)}</td>
                    <td>{fmtCurrency(r.total_fee)}</td>
                    <td>
                      {[r.licence_option && `${LICENCE_LABELS[r.licence_option]} ${fmtCurrency(r.licence_fee)}`,
                        r.day_membership && `Memb ${fmtCurrency(r.membership_fee)}`].filter(Boolean).join(' + ') || '—'}
                    </td>
                    <td>{r.cheque_amount != null ? fmtCurrency(r.cheque_amount) : '—'}</td>
                    <td>{r.bacs_amount != null ? fmtCurrency(r.bacs_amount) : '—'}</td>
                    <td>{fmtDate(r.submitted_at)}</td>
                    <td>{r.refunded_amount != null ? <span style={{ color: 'var(--danger-color)' }}>-{fmtCurrency(r.refunded_amount)}</span> : '—'}</td>
                    <td>{fmtAdjustment(r.adjustment, r.total_fee)}</td>
                    <td className="note-col">
                      {r.machines.every(m => m.submission_status === 'received') ? <NoNoteYet /> : (
                        <NoteCell
                          value={r.note}
                          label={[r.rider_first, r.rider_last].filter(Boolean).join(' ')}
                          onSave={newNote => handleNoteUpdate(r, newNote)}
                        />
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              {pricesSet && (
                <tfoot>
                  <tr>
                    <td colSpan={2}><strong>Total owed</strong></td>
                    <td><strong>{fmtCurrency(totalOwed)}</strong></td>
                    <td colSpan={7} />
                  </tr>
                  {(totalLicence > 0 || totalMembership > 0) && (
                    <>
                      <tr>
                        <td colSpan={2} style={{ paddingLeft: '1.5rem' }}>Entry fees</td>
                        <td>{fmtCurrency(totalOwed - totalLicence - totalMembership)}</td>
                        <td colSpan={7} />
                      </tr>
                      {totalLicence > 0 && (
                        <tr>
                          <td colSpan={2} style={{ paddingLeft: '1.5rem' }}>Day licence / Give It A Go</td>
                          <td>{fmtCurrency(totalLicence)}</td>
                          <td colSpan={7} />
                        </tr>
                      )}
                      {totalMembership > 0 && (
                        <tr>
                          <td colSpan={2} style={{ paddingLeft: '1.5rem' }}>Day membership</td>
                          <td>{fmtCurrency(totalMembership)}</td>
                          <td colSpan={7} />
                        </tr>
                      )}
                    </>
                  )}
                  <tr>
                    <td colSpan={2}>Cheque/PP/Cash received</td>
                    <td>{fmtCurrency(totalCheque)}</td>
                    <td colSpan={7} />
                  </tr>
                  <tr>
                    <td colSpan={2}>BACS received</td>
                    <td>{fmtCurrency(totalBacsActual)}</td>
                    <td colSpan={7} />
                  </tr>
                  {totalRefunded > 0 && (
                    <tr>
                      <td colSpan={2}>Refunded</td>
                      <td style={{ color: 'var(--danger-color)' }}>-{fmtCurrency(totalRefunded)}</td>
                      <td colSpan={7} />
                    </tr>
                  )}
                  <tr>
                    <td colSpan={2}>{totalOutstanding < -0.005 ? 'Overpaid' : 'Outstanding'}</td>
                    <td style={{ color: totalOutstanding > 0.005 ? '#c62828' : '#2e7d32', fontWeight: 600 }}>
                      {fmtCurrency(Math.abs(totalOutstanding))}
                    </td>
                    <td colSpan={7} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {bacsGroupList.length > 0 && (
            <div style={{ marginTop: '1.5rem' }}>
              <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem', color: '#333' }}>BACS Reconciliation</h3>
              <div className="entries-table-wrap">
                <table className="entries-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Entries</th>
                      <th>Fee Total</th>
                      <th title="Amount stated by payer / entered by event secretary">Stated</th>
                      <th title="Amount actually received — enter from bank statement">Actual Received</th>
                      <th title="Difference between actual received and fee total">Adjustment</th>
                      <th className="note-col">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bacsGroupList.map(g => {
                      const feesKnown = g.fees_total > 0;
                      const adjustment = g.bacs_actual_amount != null && feesKnown ? +(g.fees_total - g.bacs_actual_amount).toFixed(2) : null;
                      return (
                        <tr key={g.bac_reference} style={adjustment != null && Math.abs(adjustment) > 0.005 ? { background: '#fff3e0' } : {}}>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.88rem' }}>{g.bac_reference}</td>
                          <td style={{ fontSize: '0.82rem' }}>
                            {g.entries.map((e, i) => (
                              <span key={`${e.submission_id || 'manual'}-${e.race_number}`}>
                                {i > 0 && ', '}
                                <button
                                  type="button"
                                  onClick={() => setDetailEntry({ bac_reference: g.bac_reference, race_number: e.race_number })}
                                  style={{ background: 'none', border: 'none', padding: 0, color: 'var(--link-color, #1565c0)', cursor: 'pointer', font: 'inherit', textDecoration: 'underline' }}
                                >
                                  #{e.race_number} {[e.rider_first, e.rider_last].filter(Boolean).join(' ')}
                                </button>
                              </span>
                            ))}
                          </td>
                          <td>{feesKnown ? fmtCurrency(g.fees_total) : '—'}</td>
                          <td>{g.bacs_ref_total != null ? fmtCurrency(g.bacs_ref_total) : g.bacs_amount_total > 0 ? fmtCurrency(g.bacs_amount_total) : '—'}</td>
                          <td>
                            <InlineEditCell
                              value={g.bacs_actual_amount}
                              inputType="number"
                              prefix="£"
                              placeholder="0.00"
                              onSave={val => handleActualUpdate(g.bac_reference, val)}
                            />
                          </td>
                          <td>{adjustment != null ? fmtAdjustment(adjustment, g.fees_total) : '—'}</td>
                          <td className="note-col">
                            {g.entries.map(e => (
                              <div key={`${e.submission_id || 'manual'}-${e.race_number}`} style={{ marginBottom: g.entries.length > 1 ? '2px' : 0 }}>
                                {g.entries.length > 1 && <span style={{ fontSize: '0.75rem', color: '#888', marginRight: '4px' }}>#{e.race_number}</span>}
                                {e.submission_status === 'received' ? <NoNoteYet /> : (
                                  <NoteCell
                                    value={e.note}
                                    label={`#${e.race_number} ${[e.rider_first, e.rider_last].filter(Boolean).join(' ')}`}
                                    onSave={newNote => handleNoteUpdate(e, newNote)}
                                  />
                                )}
                              </div>
                            ))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {(() => {
                      const totFees     = bacsGroupList.reduce((s, g) => s + g.fees_total, 0);
                      const totExpected = bacsGroupList.reduce((s, g) => s + (g.bacs_ref_total != null ? g.bacs_ref_total : (g.bacs_amount_total || 0)), 0);
                      const totActual   = bacsGroupList.reduce((s, g) => s + (g.bacs_actual_amount || 0), 0);
                      const anyFees     = bacsGroupList.some(g => g.fees_total > 0);
                      const anyActual   = bacsGroupList.some(g => g.bacs_actual_amount != null);
                      const totAdj      = anyActual && anyFees ? +(totFees - totActual).toFixed(2) : null;
                      return (
                        <tr>
                          <td colSpan={2}><strong>Totals</strong></td>
                          <td><strong>{anyFees ? fmtCurrency(totFees) : '—'}</strong></td>
                          <td><strong>{fmtCurrency(totExpected)}</strong></td>
                          <td><strong>{anyActual ? fmtCurrency(totActual) : '—'}</strong></td>
                          <td><strong>{totAdj != null ? fmtAdjustment(totAdj, totFees) : '—'}</strong></td>
                          <td />
                        </tr>
                      );
                    })()}
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {detailEntry && (() => {
        // Re-derived from the current bacsGroupList (not a stale snapshot)
        // so the modal reflects handleActualUpdate's optimistic update
        // immediately, without needing to close/reopen it.
        const liveGroup = bacsGroupList.find(g => g.bac_reference === detailEntry.bac_reference);
        const liveEntry = liveGroup?.entries.find(e => e.race_number === detailEntry.race_number);
        if (!liveGroup || !liveEntry) return null;
        return (
          <BacsEntryDetailModal
            entry={liveEntry}
            group={liveGroup}
            onSaveActual={val => handleActualUpdate(liveGroup.bac_reference, val)}
            onSaveNote={newNote => handleNoteUpdate(liveEntry, newNote)}
            onClose={() => setDetailEntry(null)}
          />
        );
      })()}

      {requestingRefundFor && (
        <RefundRequestForm
          entry={requestingRefundFor}
          event={event}
          sessionToken={sessionToken}
          onClose={() => setRequestingRefundFor(null)}
          onSubmitted={load}
        />
      )}
    </div>
  );
}

// ─── Request a refund ──────────────────────────────────────────────────────
//
// Standalone action, reachable regardless of the linked submission's own
// status (received/pending/accepted/rejected/cancelled) — a refund is never
// itself a status-machine state (see cancel/reject in SubmissionActionDialog,
// which deliberately don't bundle this). Financial secretary's own
// RefundRequestsList processes what this creates.

export function RefundRequestForm({ entry, event, sessionToken, onClose, onSubmitted, initialAmount = '' }) {
  const [amount, setAmount] = useState(initialAmount);
  const [note, setNote]     = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]   = useState(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await api(
        `/api/secretary/entries/${event.CalendarID}/submissions/${entry.submission_id}/refund-request`,
        { method: 'POST', body: JSON.stringify({ amount: parseFloat(amount), note }) },
        sessionToken
      );
      onSubmitted();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sec-overlay">
      <div className="sec-modal" style={{ maxWidth: '420px' }}>
        <div className="sec-modal-header">
          <h3>Request Refund — #{entry.race_number} {[entry.rider_first, entry.rider_last].filter(Boolean).join(' ')}</h3>
          <button className="sec-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="sec-modal-body">
          <div className="form-group">
            <label>Amount (£)</label>
            <input type="number" step="0.01" min="0" value={amount}
                   onChange={e => setAmount(e.target.value)} placeholder="0.00" />
          </div>
          <div className="form-group">
            <label>Note (reason)</label>
            <textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
                      style={{ resize: 'vertical' }} placeholder="e.g. Overpaid by £10 on BACS transfer" />
          </div>
          {error && (
            <div className="error-message">
              <p>⚠️ {error}</p>
              <button onClick={() => setError(null)}>Dismiss</button>
            </div>
          )}
        </div>
        <div className="sec-modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={submit} disabled={submitting || !amount || !note.trim()}>
            {submitting ? 'Working…' : 'Request Refund'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Status pill with its own action dropdown ─────────────────────────────────
//
// Used wherever a submission's status pill is clickable (SubmissionsList,
// EntryList's "Pending" badge) — picking an option here is just a faster
// entry point into SubmissionActionDialog: onPick still opens the same
// dialog, jumped straight to that action's confirm step (see initialAction/
// autoJumpedRef there), rather than handling anything inline itself. When
// there's only one possible option (e.g. an accepted entry's only action is
// Cancel), render a plain button instead — a one-item dropdown is just
// friction for no choice.

function DecidePill({ label, className, style, options, onPick }) {
  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null); // viewport coords, computed on open
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        menuRef.current && !menuRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (options.length === 0) return null;

  if (options.length === 1) {
    return (
      <button type="button" className={className} style={style} onClick={() => onPick(options[0].action)} title={options[0].label}>
        {label}
      </button>
    );
  }

  const toggleOpen = () => {
    if (!open && btnRef.current) {
      // Rendered via a portal into <body> (see below) so it can't be clipped
      // by an ancestor table wrapper's overflow:auto — positioned from the
      // trigger button's own viewport rect rather than CSS-relative, since a
      // portalled node has no meaningful position in the table's own layout.
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ bottom: window.innerHeight - rect.top + 4, left: rect.left });
    }
    setOpen(o => !o);
  };

  return (
    <>
      <button type="button" ref={btnRef} className={className} style={style} onClick={toggleOpen} title="Click to decide this submission">
        {label} ▾
      </button>
      {open && menuPos && createPortal(
        <div className="action-menu-portal" ref={menuRef} style={{ bottom: menuPos.bottom, left: menuPos.left }}>
          {options.map(opt => (
            <button
              key={opt.action}
              type="button"
              className={`action-menu-item${opt.danger ? ' action-menu-item-danger' : ''}`}
              onClick={() => { setOpen(false); onPick(opt.action); }}
            >
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Entries received (read-only) ─────────────────────────────────────────────

// Whether the financial secretary has confirmed the bank transfer paying
// for this entry (its own, or its payer's for a covered entry): green once
// the amount received covers what was stated, orange if it's less. Nothing
// for cheques, or before it's confirmed.
function BacsReceivedNote({ submission: s }) {
  const covered = s.payment_method === 'covered';
  if (s.payment_method !== 'bacs' && !covered) return null;
  const actual = covered ? s.payer_bacs_actual_amount : s.bacs_actual_amount;
  const stated = covered ? s.payer_payment_amount : s.payment_amount;
  if (actual == null) return null;
  const short = stated != null && actual < stated - 0.005;
  return (
    <div style={{ fontSize: '0.8rem', fontWeight: 600, color: short ? '#e65100' : '#2e7d32' }}
         title="Confirmed by the financial secretary against the bank statement">
      {short
        ? `Received £${Number(actual).toFixed(2)} of £${Number(stated).toFixed(2)}`
        : `✓ Received £${Number(actual).toFixed(2)}`}
      {covered && ' (by the payer)'}
    </div>
  );
}

function SubmissionsTable({ submissions, onView, onDecide, onPrint }) {
  return (
    <div className="entries-table-wrap">
      <table className="entries-table">
        <thead>
          <tr>
            <th>Race No.</th>
            <th>Rider</th>
            <th>Passenger</th>
            <th>Machine(s)</th>
            <th>Payment</th>
            <th className="note-col">Note to Secretary</th>
            <th>Submitted</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {submissions.map(s => {
            const r = s.payload?.rider || {};
            const p = s.payload?.passenger;
            const machines = s.payload?.machines || [];
            // Riding No. preferred — no official race number exists yet, since
            // that's only assigned once a submission is accepted into entry_list.
            const raceNo = machines.map(m => m.ridingNo).filter(Boolean).join(', ') || '—';
            const machineSummary =
              machines.length === 0 ? '—' :
              machines.length === 1 ?
                [machines[0].make, machines[0].model].filter(Boolean).join(' ') +
                (machines[0].vehicleType ? ` (${machines[0].vehicleType})` : '') :
              `${machines.length} machines`;
            return (
              <tr key={s.id}>
                <td>{raceNo}</td>
                <td>
                  <button
                    type="button"
                    onClick={() => onView(s)}
                    style={{ background: 'none', border: 'none', padding: 0, font: 'inherit',
                             color: 'var(--primary-color)', textDecoration: 'underline', cursor: 'pointer' }}
                  >
                    {[r.firstName, r.surname].filter(Boolean).join(' ') || '—'}
                  </button>
                </td>
                <td>{s.payload?.hasPassenger && p ? [p.firstName, p.surname].filter(Boolean).join(' ') : '—'}</td>
                <td>{machineSummary}</td>
                <td>
                  {s.payment_method?.toUpperCase()} £{Number(s.payment_amount).toFixed(2)}
                  {s.licence_option && (
                    <div style={{ fontSize: '0.8rem' }}>
                      {s.licence_option === 'giag' ? 'Give It A Go' : 'Day licence'} £{Number(s.licence_fee).toFixed(2)}
                    </div>
                  )}
                  {s.day_membership && (
                    <div style={{ fontSize: '0.8rem' }}>
                      Day membership £{Number(s.membership_fee).toFixed(2)}
                    </div>
                  )}
                  {s.payment_method === 'bacs' && s.payment_bank_reference && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>
                      Ref: {s.payment_bank_reference}
                    </div>
                  )}
                  <BacsReceivedNote submission={s} />
                </td>
                <td className="note-col" title={s.payload?.secretaryNote || ''}
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.payload?.secretaryNote || '—'}
                </td>
                <td>{fmtDateTime(s.submitted_at)}</td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '3px' }}>
                    {(s.status === 'received' || s.status === 'pending') ? (
                      <DecidePill
                        label={s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                        className={`status-pill status-${s.status}`}
                        options={
                          s.status === 'received'
                            ? [
                                { action: 'reject', label: 'Reject', danger: true },
                                { action: 'hold', label: 'Hold for Review' },
                                { action: 'accept', label: 'Accept' },
                              ]
                            : [
                                { action: 'reject', label: 'Reject', danger: true },
                                { action: 'accept', label: 'Accept & Notify' },
                              ]
                        }
                        onPick={(action) => onDecide(s, action)}
                      />
                    ) : (
                      <span className={`status-pill status-${s.status}`}>
                        {s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                      </span>
                    )}
                    {s.refund_status && (
                      <div
                        className={`status-pill status-refund-${s.refund_status}`}
                        style={{ fontSize: '0.62rem', padding: '0.1rem 0.35rem' }}
                        title={`Refund ${refundStatusLabel(s.refund_status)}`}
                      >
                        Refund: {refundStatusLabel(s.refund_status)}
                      </div>
                    )}
                  </div>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn-edit"
                    onClick={() => onPrint(s.id)}
                    title="Print this entry as the entrant would have printed it"
                  >
                    🖨️
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SubmissionsList({ event, sessionToken, onEntryListChanged }) {
  const [allSubmissions, setAllSubmissions] = useState([]); // every status, unfiltered
  const [showOthers, setShowOthers]   = useState(false); // reveals the separate "Other Submissions" section below
  const [error, setError]             = useState(null);
  const [search, setSearch]           = useState('');
  const [initialLoad, setInitialLoad] = useState(true);
  const [viewing, setViewing]         = useState(null); // submission currently shown in the detail modal
  const [deciding, setDeciding]       = useState(null); // submission currently shown in the decide dialog
  const [decideAction, setDecideAction] = useState(null); // action picked from the pill's own dropdown, if any
  const [printing, setPrinting]       = useState(null); // submission id currently shown in the print view
  const [emailing, setEmailing]       = useState(false); // the Email entrants dialog is open

  const loadSubmissions = useCallback(async () => {
    try {
      const all = await api(`/api/secretary/entries/${event.CalendarID}/submissions`, {}, sessionToken);
      setAllSubmissions(all);
    } catch (err) {
      setError(err.message);
    } finally {
      setInitialLoad(false);
    }
  }, [event.CalendarID, sessionToken]);

  useEffect(() => { loadSubmissions(); }, [loadSubmissions]);

  const decide = (s, action) => { setDeciding(s); setDecideAction(action); };

  // "New Entries Received" is normally an inbox of unactioned submissions
  // only — once one is held (pending) or accepted, the real entry_list row
  // is what represents it from then on (see the status badge there), so it
  // drops out of view rather than lingering forever. Pending/accepted/
  // rejected/cancelled ones are still viewable (e.g. to check what was
  // rejected, or track down a submission if something's gone wrong with its
  // refund) but live in their own separate "Other Submissions" section
  // below rather than merged into this table — otherwise a rejected
  // submission from weeks ago sorts in right next to a genuinely new one,
  // and the inbox stops reading as "what's actually new."
  const received = allSubmissions.filter(s => s.status === 'received');
  const others   = allSubmissions.filter(s => s.status !== 'received');

  const q = search.toLowerCase();
  const matchesSearch = (s) => {
    const r = s.payload?.rider || {};
    const p = s.payload?.passenger || {};
    return [r.firstName, r.surname, p.firstName, p.surname,
            s.payment_method, s.payment_bank_reference]
      .some(v => v && String(v).toLowerCase().includes(q));
  };
  const filteredReceived = q ? received.filter(matchesSearch) : received;
  const filteredOthers   = q ? others.filter(matchesSearch)   : others;

  return (
    <>
      <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
        <div className="sec-section-header">
          <h2>📥 New Entries Received</h2>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button className="btn-add-entry" onClick={() => setEmailing(true)}
                    title="Send one email to every entrant in the groups you choose, e.g. final instructions">
              ✉️ Email entrants
            </button>
            <span className="entry-count-badge">
              {filteredReceived.length} / {received.length} submission{received.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: '1rem' }}>
            <p>⚠️ {error}</p>
            <button onClick={() => setError(null)}>Dismiss</button>
          </div>
        )}

        {initialLoad ? (
          <Spinner />
        ) : (
          <>
            <div className="entries-toolbar">
              <input
                className="entries-search"
                placeholder="Search rider, passenger, or payment reference"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap', fontSize: '0.9rem' }}>
                <input type="checkbox" checked={showOthers} onChange={e => setShowOthers(e.target.checked)} />
                Show other submissions (pending/accepted/rejected/cancelled)
              </label>
            </div>
            {filteredReceived.length === 0 ? (
              <EmptyState
                icon="📭"
                message={search ? 'No submissions match your search.' : 'No new entries waiting for review.'}
              />
            ) : (
              <SubmissionsTable
                submissions={filteredReceived}
                event={event}
                onView={setViewing}
                onDecide={decide}
                onPrint={setPrinting}
              />
            )}
          </>
        )}
      </div>

      {showOthers && !initialLoad && (
        <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
          <div className="sec-section-header">
            <h2>🗂️ Other Submissions</h2>
            <span className="entry-count-badge">
              {filteredOthers.length} / {others.length} submission{others.length !== 1 ? 's' : ''}
            </span>
          </div>
          {filteredOthers.length === 0 ? (
            <EmptyState
              icon="🗂️"
              message={search ? 'No submissions match your search.' : 'No pending, accepted, rejected, or cancelled submissions yet.'}
            />
          ) : (
            <SubmissionsTable
              submissions={filteredOthers}
              event={event}
              onView={setViewing}
              onDecide={decide}
              onPrint={setPrinting}
            />
          )}
        </div>
      )}

      {viewing && <SubmissionDetailModal submission={viewing} onClose={() => setViewing(null)} />}
      {emailing && <EmailEntrantsDialog event={event} sessionToken={sessionToken} onClose={() => setEmailing(false)} />}
      {deciding && (
        <SubmissionActionDialog
          submission={deciding}
          event={event}
          sessionToken={sessionToken}
          initialAction={decideAction}
          onClose={() => { setDeciding(null); setDecideAction(null); }}
          onActioned={() => { setDeciding(null); setDecideAction(null); loadSubmissions(); onEntryListChanged?.(); }}
        />
      )}
      {printing && (
        <SubmissionPrintView
          calendarId={event.CalendarID}
          submissionId={printing}
          sessionToken={sessionToken}
          onClose={() => setPrinting(null)}
        />
      )}
    </>
  );
}

// ─── Refund requests (financial secretary's queue) ────────────────────────────
//
// Structurally mirrors SubmissionsList above — a self-fetching inbox that
// drops a row once it's been actioned (mark sent / decline), same as
// SubmissionsList drops a row once it's held/accepted. Fetches ALL non-null
// refund_status rows (see get_refund_requests in secretary_routes.py) but
// only shows the active 'requested' queue by default — "Show refunded"
// reveals 'sent' ones too, e.g. to double-check a refund that was just paid.

function RefundRequestsList({ event, sessionToken, onEntryListChanged }) {
  const [allRequests, setAllRequests] = useState([]); // every non-null refund_status
  const [showRefunded, setShowRefunded] = useState(false);
  const [error, setError]             = useState(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [deciding, setDeciding]       = useState(null); // refund request currently shown in the decide dialog

  const loadRequests = useCallback(async () => {
    try {
      const all = await api(`/api/secretary/entries/${event.CalendarID}/refund-requests`, {}, sessionToken);
      setAllRequests(all);
    } catch (err) {
      setError(err.message);
    } finally {
      setInitialLoad(false);
    }
  }, [event.CalendarID, sessionToken]);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  if (initialLoad) return <Spinner />;
  if (allRequests.length === 0) return null; // nothing has ever needed a refund for this event

  const neededCount = allRequests.filter(s => s.refund_status === 'needed').length;
  const requests = showRefunded
    ? allRequests.filter(s => s.refund_status === 'needed' || s.refund_status === 'sent')
    : allRequests.filter(s => s.refund_status === 'needed');

  return (
    <div className="sec-section" style={{ marginBottom: '1.25rem' }}>
      <div className="sec-section-header">
        <h2>💷 Refund Requests</h2>
        <span className="entry-count-badge">
          {neededCount} pending
        </span>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          <p>⚠️ {error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      <div className="entries-toolbar">
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', whiteSpace: 'nowrap', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={showRefunded} onChange={e => setShowRefunded(e.target.checked)} />
          Show refunded
        </label>
      </div>

      {requests.length === 0 ? (
        <EmptyState icon="💷" message="No pending refund requests." />
      ) : (
        <div className="entries-table-wrap">
          <table className="entries-table">
            <thead>
              <tr>
                <th>Race No.</th>
                <th>Rider</th>
                <th>Submission Status</th>
                <th>Amount</th>
                <th className="note-col">Note</th>
                <th>Requested</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {requests.map(s => {
                const r = s.payload?.rider || {};
                const machines = s.payload?.machines || [];
                const raceNo = machines.map(m => m.ridingNo).filter(Boolean).join(', ') || '—';
                return (
                  <tr key={s.id}>
                    <td>{raceNo}</td>
                    <td>{[r.firstName, r.surname].filter(Boolean).join(' ') || '—'}</td>
                    <td><span className={`status-pill status-${s.status}`}>{s.status}</span></td>
                    <td>{fmtCurrency(s.refund_status === 'needed' ? s.refund_requested_amount : s.refund_amount)}</td>
                    <td className="note-col" title={s.refund_note || ''}
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.refund_note || '—'}
                    </td>
                    <td>
                      {s.refund_requested_by || '—'}
                      {s.refund_requested_at ? <div style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>{fmtDateTime(s.refund_requested_at)}</div> : null}
                    </td>
                    <td>
                      {s.refund_status === 'needed' ? (
                        <button
                          type="button"
                          className="status-pill status-refund-needed"
                          onClick={() => setDeciding(s)}
                          title="Click to process this refund request"
                        >
                          {refundStatusLabel(s.refund_status)}
                        </button>
                      ) : (
                        <span className={`status-pill status-refund-${s.refund_status}`}>
                          {refundStatusLabel(s.refund_status)}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {deciding && (
        <RefundActionDialog
          submission={deciding}
          event={event}
          sessionToken={sessionToken}
          onClose={() => setDeciding(null)}
          onActioned={() => { loadRequests(); onEntryListChanged?.(); }}
        />
      )}
    </div>
  );
}

// Same key→label mapping as CLASS_FLAGS in OnlineEntryForm.jsx — kept as an
// independent copy since this is a different component/file; update both
// together if the online entry form's class list ever changes.
const SUBMISSION_CLASS_FLAGS = [
  { key: 'openCapacity', label: 'Open Capacity' },
  { key: 'forgottenEra', label: 'Forgotten Era' },
  { key: 'roadLegal',    label: 'Road Legal' },
  { key: 'vintage',      label: 'Vintage' },
  { key: 'classic',      label: 'Classic' },
];

// Always renders label + value — this is meant to read like the printed
// entry form, so nothing is ever selectively hidden just because a field
// is blank; blank shows as an explicit "—", never a vanished row.
const Field = ({ label, value }) => (
  <div style={{ marginBottom: '0.4rem' }}>
    <strong>{label}:</strong> {value || value === 0 ? value : <span style={{ color: 'var(--text-light)' }}>—</span>}
  </div>
);

function SubmissionDetailModal({ submission, onClose }) {
  const pl = submission.payload || {};
  const r = pl.rider || {};
  const p = pl.passenger || {};
  const entrant = pl.entrant || {};
  const machines = pl.machines || [];

  let declarations = null;
  try {
    declarations = submission.declaration_text ? JSON.parse(submission.declaration_text) : null;
  } catch {
    declarations = null;
  }

  return (
    <div className="sec-overlay">
      <div className="sec-modal" style={{ maxWidth: '720px', maxHeight: '85vh', overflowY: 'auto' }}>
        <div className="sec-modal-header">
          <h3>Entry Submission — {[r.firstName, r.surname].filter(Boolean).join(' ') || 'Unknown rider'}</h3>
          <button className="sec-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="sec-modal-body">
          <h4>Rider</h4>
          <Field label="First name(s)" value={r.firstName} />
          <Field label="Surname" value={r.surname} />
          <Field label="Email address" value={r.email} />
          <Field label="Telephone" value={r.tel} />
          <Field label="Address" value={r.address} />
          <Field label="Postcode" value={r.postcode} />
          <Field label="Date of birth" value={r.dob} />
          <Field label="ACU Licence / Registration No." value={r.acuLicence} />
          <Field label="ACU Affiliated Club" value={r.acuClub} />
          <Field label="Rider's emergency contact (name & number)" value={pl.riderEmergencyContact} />

          <h4>Passenger</h4>
          <Field label="Has passenger" value={pl.hasPassenger ? 'Yes' : 'No'} />
          <Field label="First name(s)" value={p.firstName} />
          <Field label="Surname" value={p.surname} />
          <Field label="Address" value={p.address} />
          <Field label="Postcode" value={p.postcode} />
          <Field label="Date of birth" value={p.dob} />
          <Field label="Telephone" value={p.tel} />
          <Field label="ACU Licence / Registration No." value={p.acuLicence} />
          <Field label="ACU Affiliated Club" value={p.acuClub} />
          <Field label="Passenger's emergency contact (name & number)" value={pl.passengerEmergencyContact} />

          <h4>Entrant (if different from rider)</h4>
          <Field label="Entrant differs from rider" value={pl.entrantDiffers ? 'Yes' : 'No'} />
          <Field label="Company / Name" value={entrant.name} />
          <Field label="Address" value={entrant.address} />
          <Field label="Entrant's Licence No." value={entrant.licenceNo} />

          <h4>Machine{machines.length !== 1 ? 's' : ''}</h4>
          {machines.length === 0 && <p style={{ color: 'var(--text-light)' }}>None listed.</p>}
          {machines.map((m, i) => (
            <div key={i} style={{ marginBottom: '0.75rem', paddingBottom: '0.75rem',
                                   borderBottom: i < machines.length - 1 ? '1px solid #eee' : 'none' }}>
              {machines.length > 1 && <div style={{ fontWeight: 600, marginBottom: '0.3rem' }}>Machine {i + 1}</div>}
              <Field label="Riding No. preferred" value={m.ridingNo} />
              <Field label="Machine model" value={m.model} />
              <Field label="Vehicle type" value={m.vehicleType} />
              <Field label="Make" value={m.make} />
              <Field label="Capacity (cc)" value={m.capacity} />
              <Field label="Stroke (mm)" value={m.stroke} />
              {SUBMISSION_CLASS_FLAGS.map(f => (
                <Field key={f.key} label={f.label} value={m.classFlags?.[f.key] ? 'Yes' : 'No'} />
              ))}
            </div>
          ))}

          <h4>Payment</h4>
          <Field label="Payment method" value={submission.payment_method === 'cheque' ? 'Cheque' : 'Bank Transfer (BACS)'} />
          <Field label={`AMOUNT (${submission.payment_method === 'cheque' ? 'Cheque' : 'Bank Transfer'})`}
                 value={`£${Number(submission.payment_amount).toFixed(2)}`} />
          <Field label="Bank transfer reference used" value={submission.payment_bank_reference} />
          <Field label="This payment also covers other race numbers" value={submission.payment_race_numbers} />

          <h4>Note to secretary</h4>
          <Field label="Note" value={pl.secretaryNote} />

          <h4>Declaration</h4>
          {declarations ? (
            <details style={{ marginBottom: '0.75rem' }}>
              <summary style={{ cursor: 'pointer', color: 'var(--primary-color)' }}>
                Show full declaration text shown at signing (v{submission.declaration_version})
              </summary>
              <ul style={{ marginTop: '0.5rem' }}>
                {(declarations.declarations || []).map((d, i) => <li key={i} style={{ marginBottom: '0.4rem' }}>{d}</li>)}
              </ul>
              {declarations.riskAcknowledgement && (
                <p><strong>Acknowledgement of the risks of motorsport:</strong> {declarations.riskAcknowledgement}</p>
              )}
            </details>
          ) : (
            <Field label="Declaration version" value={submission.declaration_version} />
          )}
          <Field label="Agreed to declarations" value={submission.signature_agreed ? 'Yes' : 'No'} />

          <h4>Signatures</h4>
          <Field label="Rider's typed signature (full name)" value={submission.signature_rider_name} />
          <Field label="Rider signature date" value={submission.signature_rider_date} />
          <Field label="Passenger's typed signature (full name)" value={submission.signature_passenger_name} />
          <Field label="Passenger signature date" value={submission.signature_passenger_date} />
          <Field label="Parent/guardian typed signature" value={submission.signature_parent_guardian_name} />
          <Field label="Parent/guardian signature date" value={submission.signature_parent_guardian_date} />

          <h4>Submission</h4>
          <Field label="Submitted" value={fmtDateTime(submission.submitted_at)} />
          <Field label="Status" value={submission.status} />
        </div>
        <div className="sec-modal-footer">
          <button className="btn-save" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Shared class lists ───────────────────────────────────────────────────────

const VC_CLASSES = [
  '250V', '350V', '500V', '750V', '1300V',
  '250C', '350C', '500C', '750C', '1300C',
  'SidecarV', 'SidecarC',
];

const FE_CLASSES = ['FEUNDER400', 'FEOVER400', 'FESIDECAR'];

// Shared react-select styling so the Hill picker matches the surrounding
// .form-group inputs (2px border, same radius/height) instead of react-select's defaults.
const hillSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '2.6rem',
    borderWidth: 2,
    borderColor: state.isFocused ? 'var(--primary-color)' : 'var(--border-color)',
    borderRadius: 6,
    boxShadow: 'none',
    '&:hover': { borderColor: state.isFocused ? 'var(--primary-color)' : 'var(--border-color)' },
  }),
  menu: base => ({ ...base, zIndex: 20 }),
};

function hillOptionsFrom(rows) {
  return [...new Set(rows.map(r => r.hill))].sort().map(h => ({ value: h, label: h }));
}

// ─── Generic records editor ───────────────────────────────────────────────────

function RecordsEditor({ sessionToken, classes, apiPath, title, icon, description }) {
  const emptyForm = { hill: '', class: classes[0], record: '', recordset: '', rider_first: '', rider_last: '', machine_mfr: '', machine_model: '' };

  const [records, setRecords]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [showAdd, setShowAdd]   = useState(false);
  const [addForm, setAddForm]   = useState(emptyForm);
  const [addErr, setAddErr]     = useState(null);
  const [adding, setAdding]     = useState(false);
  const [search, setSearch]     = useState('');
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRecords(await api(apiPath, {}, sessionToken));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [sessionToken, apiPath]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    setAddErr(null);
    setAdding(true);
    try {
      const row = await api(
        apiPath,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(addForm) },
        sessionToken,
      );
      setRecords(prev => [...prev, row].sort((a, b) =>
        a.hill.localeCompare(b.hill) || classes.indexOf(a.class) - classes.indexOf(b.class) || (b.recordset || '').localeCompare(a.recordset || '')
      ));
      setAddForm(emptyForm);
      setShowAdd(false);
    } catch (e) {
      setAddErr(e.message);
    } finally {
      setAdding(false);
    }
  };

  const setAF = (field, value) => setAddForm(f => ({ ...f, [field]: value }));

  const q = search.toLowerCase();
  const filtered = q
    ? records.filter(r =>
        [r.hill, r.class, String(r.record), r.recordset,
         r.rider_first, r.rider_last, r.machine_mfr, r.machine_model]
          .some(v => v && v.toLowerCase().includes(q))
      )
    : records;

  // Records arrive sorted latest-first per hill+class, so the first occurrence
  // of each hill+class pair is the current record — that's what "collapsed" shows.
  const displayed = expanded ? filtered : (() => {
    const seen = new Set();
    return filtered.filter(r => {
      const key = `${r.hill}||${r.class}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  })();

  const hills = [...new Set(displayed.map(r => r.hill))].sort();
  const hillOptions = hillOptionsFrom(records);

  const addForm_ = (
    showAdd ? (
      <div style={{ background: 'var(--bg-color)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.25rem' }}>
        <p className="form-section-label" style={{ marginBottom: '0.75rem' }}>Add Record</p>
        {addErr && <div className="error-message" style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem' }}>{addErr}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div className="form-group">
            <label>Hill</label>
            <CreatableSelect
              styles={hillSelectStyles}
              value={addForm.hill ? { value: addForm.hill, label: addForm.hill } : null}
              onChange={opt => setAF('hill', opt ? opt.value : '')}
              options={hillOptions}
              placeholder="Select or type a new hill…"
              isClearable
              autoFocus
              formatCreateLabel={v => `Add new hill "${v}"`}
            />
          </div>
          <div className="form-group"><label>Class</label>
            <select value={addForm.class} onChange={e => setAF('class', e.target.value)}>
              {classes.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group"><label>Record (s)</label><input type="number" step="0.01" min="0.01" value={addForm.record} onChange={e => setAF('record', e.target.value)} placeholder="e.g. 27.83" /></div>
          <div className="form-group"><label>Date Set</label><input type="date" value={addForm.recordset} onChange={e => setAF('recordset', e.target.value)} /></div>
          <div className="form-group"><label>Rider First</label><input type="text" value={addForm.rider_first} onChange={e => setAF('rider_first', e.target.value)} /></div>
          <div className="form-group"><label>Rider Last</label><input type="text" value={addForm.rider_last} onChange={e => setAF('rider_last', e.target.value)} /></div>
          <div className="form-group"><label>Manufacturer</label><input type="text" value={addForm.machine_mfr} onChange={e => setAF('machine_mfr', e.target.value)} /></div>
          <div className="form-group"><label>Model</label><input type="text" value={addForm.machine_model} onChange={e => setAF('machine_model', e.target.value)} /></div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn-save" onClick={handleAdd} disabled={adding}>Add Record</button>
          <button className="btn-cancel" onClick={() => { setShowAdd(false); setAddErr(null); setAddForm(emptyForm); }} disabled={adding}>Cancel</button>
        </div>
      </div>
    ) : (
      <button className="btn-add-entry" style={{ marginBottom: '1.25rem' }} onClick={() => setShowAdd(true)}>
        + Add Record
      </button>
    )
  );

  return (
    <div className="sec-section">
      <div className="sec-section-header">
        <h2>{icon} {title}</h2>
        <span className="entry-count-badge">{displayed.length}{displayed.length !== records.length ? ` / ${records.length}` : ''} record{records.length !== 1 ? 's' : ''}</span>
      </div>
      <p style={{ color: 'var(--text-light)', marginBottom: '1.25rem', fontSize: '0.9rem' }}>{description}</p>

      {loading && <Spinner />}
      {error && <div className="error-message"><h3>⚠️ Error</h3><p>{error}</p></div>}

      {!loading && !error && (
        <>
          {addForm_}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '1.25rem' }}>
            <input
              className="entries-search"
              placeholder="Search hill, class, rider, machine…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, display: 'block', boxSizing: 'border-box' }}
            />
            <button className="btn-edit" style={{ whiteSpace: 'nowrap' }} onClick={() => setExpanded(x => !x)}>
              {expanded ? '▲ Show latest only' : '▼ Show full history'}
            </button>
          </div>
          {displayed.length === 0 ? (
            records.length === 0
              ? <EmptyState icon={icon} message="No records found." />
              : <EmptyState icon="🔍" message="No records match your search." />
          ) : (
            hills.map(hill => {
              const hillRecs = displayed.filter(r => r.hill === hill);
              return (
                <div key={hill} style={{ marginBottom: '1.5rem' }}>
                  <p className="form-section-label" style={{ marginBottom: '0.5rem' }}>{hill}</p>
                  <div className="entries-table-wrap">
                    <table className="entries-table">
                      <thead>
                        <tr>
                          <th>Class</th>
                          <th>Record (s)</th>
                          <th>Date Set</th>
                          <th>Rider</th>
                          <th>Machine</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hillRecs.map(rec => (
                          <tr key={rec.id}>
                            <td><strong>{rec.class}</strong></td>
                            <td>{rec.record.toFixed(2)}</td>
                            <td>{rec.recordset || '—'}</td>
                            <td>{[rec.rider_first, rec.rider_last].filter(Boolean).join(' ') || '—'}</td>
                            <td>{[rec.machine_mfr, rec.machine_model].filter(Boolean).join(' ') || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}

        </>
      )}
    </div>
  );
}

// ─── VC Targets editor ────────────────────────────────────────────────────────

function VCTargetsEditor({ sessionToken }) {
  const [targets, setTargets]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [editKey, setEditKey]     = useState(null);   // 'hill||class' being edited
  const [editVal, setEditVal]     = useState('');
  const [saving, setSaving]       = useState(false);
  const [deleteKey, setDeleteKey] = useState(null);   // pending confirm
  const [showAdd, setShowAdd]     = useState(false);
  const [addHill, setAddHill]     = useState('');
  const [addCls, setAddCls]       = useState('250V');
  const [addTarget, setAddTarget] = useState('');
  const [addErr, setAddErr]       = useState(null);
  const [search, setSearch]       = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await api('/api/secretary/vctargets', {}, sessionToken);
      setTargets(rows);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (hill, cls, current) => {
    setEditKey(`${hill}||${cls}`);
    setEditVal(String(current));
    setDeleteKey(null);
  };

  const cancelEdit = () => { setEditKey(null); setEditVal(''); };

  const saveEdit = async (hill, cls) => {
    const val = parseFloat(editVal);
    if (!editVal || isNaN(val) || val <= 0) return;
    setSaving(true);
    try {
      await api(
        `/api/secretary/vctargets/${encodeURIComponent(hill)}/${encodeURIComponent(cls)}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ target: val }) },
        sessionToken,
      );
      setTargets(prev => prev.map(t =>
        t.hill === hill && t.class === cls ? { ...t, target: val } : t
      ));
      setEditKey(null);
    } catch (e) {
      alert(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (hill, cls) => {
    setSaving(true);
    try {
      await api(
        `/api/secretary/vctargets/${encodeURIComponent(hill)}/${encodeURIComponent(cls)}`,
        { method: 'DELETE' },
        sessionToken,
      );
      setTargets(prev => prev.filter(t => !(t.hill === hill && t.class === cls)));
      setDeleteKey(null);
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    setAddErr(null);
    const val = parseFloat(addTarget);
    if (!addHill.trim()) { setAddErr('Hill name is required'); return; }
    if (!addTarget || isNaN(val) || val <= 0) { setAddErr('Enter a valid target time (seconds)'); return; }
    setSaving(true);
    try {
      const row = await api(
        '/api/secretary/vctargets',
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ hill: addHill.trim(), class: addCls, target: val }) },
        sessionToken,
      );
      setTargets(prev => [...prev, row].sort((a, b) =>
        a.hill.localeCompare(b.hill) || VC_CLASSES.indexOf(a.class) - VC_CLASSES.indexOf(b.class)
      ));
      setAddHill(''); setAddTarget(''); setAddCls('250V'); setShowAdd(false);
    } catch (e) {
      setAddErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const q = search.toLowerCase();
  const filteredTargets = q
    ? targets.filter(t => [t.hill, t.class, String(t.target)].some(v => v && v.toLowerCase().includes(q)))
    : targets;
  const hills = [...new Set(filteredTargets.map(t => t.hill))].sort();
  const hillOptions = hillOptionsFrom(targets);

  const addForm_ = (
    showAdd ? (
      <div style={{ background: 'var(--bg-color)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '1.25rem', marginBottom: '1.25rem' }}>
        <p className="form-section-label" style={{ marginBottom: '0.75rem' }}>Add Target Time</p>
        {addErr && <div className="error-message" style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem' }}>{addErr}</div>}
        <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
          <div className="form-group">
            <label>Hill</label>
            <CreatableSelect
              styles={hillSelectStyles}
              value={addHill ? { value: addHill, label: addHill } : null}
              onChange={opt => setAddHill(opt ? opt.value : '')}
              options={hillOptions}
              placeholder="Select or type a new hill…"
              isClearable
              autoFocus
              formatCreateLabel={v => `Add new hill "${v}"`}
            />
          </div>
          <div className="form-group">
            <label>Class</label>
            <select value={addCls} onChange={e => setAddCls(e.target.value)}>
              {VC_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Target (seconds)</label>
            <input type="number" step="0.01" min="0.01" value={addTarget} onChange={e => setAddTarget(e.target.value)} placeholder="e.g. 28.50" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button className="btn-save" onClick={handleAdd} disabled={saving}>Add Target</button>
          <button className="btn-cancel" onClick={() => { setShowAdd(false); setAddErr(null); setAddHill(''); setAddTarget(''); setAddCls('250V'); }} disabled={saving}>Cancel</button>
        </div>
      </div>
    ) : (
      <button className="btn-add-entry" style={{ marginBottom: '1.25rem' }} onClick={() => setShowAdd(true)}>
        + Add Target Time
      </button>
    )
  );

  return (
    <div className="sec-section">
      <div className="sec-section-header">
        <h2>🎯 VC Target Times</h2>
        <span className="entry-count-badge">{filteredTargets.length}{filteredTargets.length !== targets.length ? ` / ${targets.length}` : ''} target{targets.length !== 1 ? 's' : ''}</span>
      </div>
      <p style={{ color: 'var(--text-light)', marginBottom: '1.25rem', fontSize: '0.9rem' }}>
        Target times (in seconds) used to calculate adjusted times for the Vintage-Classic championship.
      </p>

      {loading && <Spinner />}
      {error && <div className="error-message"><h3>⚠️ Error</h3><p>{error}</p></div>}

      {!loading && !error && (
        <>
          {addForm_}
          <input
            className="entries-search"
            placeholder="Search hill or class…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ marginBottom: '1.25rem', display: 'block', width: '100%', boxSizing: 'border-box' }}
          />
          {filteredTargets.length === 0 ? (
            targets.length === 0
              ? <EmptyState icon="🎯" message="No target times configured yet." />
              : <EmptyState icon="🔍" message="No targets match your search." />
          ) : (
            hills.map(hill => {
              const hillTargets = filteredTargets.filter(t => t.hill === hill);
              return (
                <div key={hill} style={{ marginBottom: '1.5rem' }}>
                  <p className="form-section-label" style={{ marginBottom: '0.5rem' }}>{hill}</p>
                  <div className="entries-table-wrap">
                    <table className="entries-table">
                      <thead>
                        <tr>
                          <th>Class</th>
                          <th>Target (s)</th>
                          <th style={{ width: '10rem' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {hillTargets.map(t => {
                          const key = `${t.hill}||${t.class}`;
                          const isEditing = editKey === key;
                          const isDeleting = deleteKey === key;
                          return (
                            <tr key={key}>
                              <td><strong>{t.class}</strong></td>
                              <td>
                                {isEditing ? (
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={editVal}
                                    onChange={e => setEditVal(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(t.hill, t.class); if (e.key === 'Escape') cancelEdit(); }}
                                    autoFocus
                                    style={{ width: '7rem', padding: '0.3rem 0.5rem', fontSize: '0.95rem', border: '2px solid var(--primary-color)', borderRadius: '4px' }}
                                  />
                                ) : (
                                  t.target.toFixed(2)
                                )}
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {isEditing ? (
                                  <>
                                    <button className="btn-save" style={{ padding: '0.3rem 0.75rem', fontSize: '0.85rem', marginRight: '0.5rem' }} onClick={() => saveEdit(t.hill, t.class)} disabled={saving}>Save</button>
                                    <button className="btn-cancel" style={{ padding: '0.3rem 0.75rem', fontSize: '0.85rem' }} onClick={cancelEdit} disabled={saving}>Cancel</button>
                                  </>
                                ) : isDeleting ? (
                                  <>
                                    <span style={{ fontSize: '0.85rem', color: 'var(--error-color)', marginRight: '0.5rem' }}>Delete?</span>
                                    <button className="btn-confirm-delete" style={{ padding: '0.3rem 0.75rem', fontSize: '0.85rem', marginRight: '0.5rem' }} onClick={() => confirmDelete(t.hill, t.class)} disabled={saving}>Yes</button>
                                    <button className="btn-cancel" style={{ padding: '0.3rem 0.75rem', fontSize: '0.85rem' }} onClick={() => setDeleteKey(null)} disabled={saving}>No</button>
                                  </>
                                ) : (
                                  <>
                                    <button className="btn-edit" style={{ padding: '0.3rem 0.75rem', fontSize: '0.85rem', marginRight: '0.5rem' }} onClick={() => startEdit(t.hill, t.class, t.target)}>Edit</button>
                                    <button className="btn-delete" style={{ padding: '0.3rem 0.75rem', fontSize: '0.85rem' }} onClick={() => { setDeleteKey(key); setEditKey(null); }}>Delete</button>
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })
          )}

        </>
      )}
    </div>
  );
}

// ─── Main portal component ────────────────────────────────────────────────────

export default function SecretaryPortal() {
  const [sessionToken, setSessionToken] = useState(null);
  const [email, setEmail]               = useState(null);
  const [role, setRole]                 = useState('secretary');
  const [view, setView]                 = useState('login');    // login | events | entries | vctargets | vcrecords | ferecords | financial-dashboard
  const [sessionExpired, setSessionExpired] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  // Where 'entries' was entered from — governs where financial roles land
  // back on ('events' browse list vs 'financial-dashboard' consolidated
  // view) and what the back button says. Irrelevant for other roles, which
  // always go back to 'events'.
  const [entriesOrigin, setEntriesOrigin] = useState('events');
  const [verifying, setVerifying]       = useState(false);
  const [verifyError, setVerifyError]   = useState(null);

  // ?event=<CalendarID> in the URL, read once on mount — lets EventList
  // auto-restore the previously-open event after a refresh (see its own
  // effect). Not kept reactive afterward; handleSelectEvent/handleBackToEvents
  // below keep the URL in sync with in-app navigation from that point on.
  const [initialEventId] = useState(() => {
    const p = new URLSearchParams(window.location.search).get('event');
    return p ? Number(p) : null;
  });

  // Read ?subclass from URL once on mount (used for login form + URL preservation)
  const urlSubclass = (() => {
    const p = new URLSearchParams(window.location.search).get('subclass');
    return p ? p.toUpperCase() : null;
  })();

  const verifyMagicLink = async (token, shared = false) => {
    setVerifying(true);
    setVerifyError(null);
    try {
      const sharedParam = shared ? '&shared=1' : '';
      const { session_token, email: verifiedEmail, role: verifiedRole } = await api(
        `/api/secretary/verify?token=${encodeURIComponent(token)}${sharedParam}`
      );
      const resolvedRole = verifiedRole || 'secretary';
      // Shared-computer: session cookies (cleared on browser close); own device: 90-day persistent
      const cookieDays = shared ? 0 : 90;
      setCookie(SESSION_COOKIE, session_token, cookieDays);
      setCookie(SESSION_EMAIL_COOKIE, verifiedEmail, cookieDays);
      setCookie(SESSION_ROLE_COOKIE, resolvedRole, cookieDays);
      setCookie(SAVED_EMAIL_COOKIE, verifiedEmail, shared ? 0 : 365);
      setSessionToken(session_token);
      setEmail(verifiedEmail);
      setRole(resolvedRole);
      setView(FINANCIAL_ROLES.has(resolvedRole) ? 'financial-dashboard' : 'events');
    } catch (err) {
      setVerifyError(err.message);
    } finally {
      setVerifying(false);
    }
  };

  // On mount: check for existing session cookie OR magic-link token in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const subclass = urlParams.get('subclass')?.toUpperCase() || null;
    const shared = urlParams.get('shared') === '1';

    if (token) {
      // Strip token (and shared flag) from URL but preserve ?subclass
      const newSearch = subclass ? `?subclass=${subclass}` : '';
      window.history.replaceState({}, '', `/secretary${newSearch}`);
      verifyMagicLink(token, shared);
      return;
    }

    const saved = getCookie(SESSION_COOKIE);
    const savedEmail = getCookie(SESSION_EMAIL_COOKIE);
    const savedRole = getCookie(SESSION_ROLE_COOKIE) || 'secretary';
    if (saved) {
      // Only restore the session if the stored role matches what the URL expects.
      // e.g. a ?subclass=FE URL must not silently restore a 'secretary' session.
      // Each URL accepts a base role plus its super-sec variant.
      const validRolesForUrl = subclass ? {
        FE:         ['fe_secretary', 'fe-super-sec'],
        VC:         ['vc_secretary', 'vc-super-sec'],
        RL:         ['rl_secretary'],
        MEMBERSHIP: ['membership', 'membership-super-sec'],
        FINANCIAL:  ['financial', 'financial-super-sec'],
      }[subclass] || ['secretary'] : ['secretary', 'event-super-sec'];
      if (validRolesForUrl.includes(savedRole)) {
        setSessionToken(saved);
        setEmail(savedEmail);
        setRole(savedRole);
        setView(FINANCIAL_ROLES.has(savedRole) ? 'financial-dashboard' : 'events');
      } else {
        // Role mismatch – show login for the correct portal, but pre-fill the email
        const loginEmail = getCookie(SAVED_EMAIL_COOKIE);
        if (loginEmail) setEmail(loginEmail);
      }
    } else {
      // No active session – pre-fill login form with saved email
      const loginEmail = getCookie(SAVED_EMAIL_COOKIE);
      if (loginEmail) setEmail(loginEmail);
    }
  }, []);

  // Auto-clear session if any authenticated API call returns 401
  useEffect(() => {
    const handler = () => {
      deleteCookie(SESSION_COOKIE);
      deleteCookie(SESSION_ROLE_COOKIE);
      // Keep email cookie so LoginScreen can pre-fill it
      setSessionToken(null);
      setRole('secretary');
      setSelectedEvent(null);
      setSessionExpired(true);
      setView('login');
      // email state is kept so it can be passed to LoginScreen
    };
    window.addEventListener('secretary-session-expired', handler);
    return () => window.removeEventListener('secretary-session-expired', handler);
  }, []);

  const handleLogout = async () => {
    try {
      await api('/api/secretary/logout', { method: 'POST' }, sessionToken);
    } catch { /* ignore */ }
    deleteCookie(SESSION_COOKIE);
    deleteCookie(SESSION_EMAIL_COOKIE);
    deleteCookie(SESSION_ROLE_COOKIE);
    setSessionToken(null);
    setEmail(getCookie(SAVED_EMAIL_COOKIE) || null);
    setRole('secretary');
    setSelectedEvent(null);
    setView('login');
    setEventUrlParam(null);
  };

  // Keeps ?event=<id> in sync with in-app navigation, preserving whatever
  // else is already in the URL (e.g. ?subclass=FINANCIAL) — so a refresh
  // (or a link copied/read aloud mid phone-call) lands back on the same
  // event's screen instead of the top-level "Your Events" list.
  const setEventUrlParam = (eventId) => {
    const params = new URLSearchParams(window.location.search);
    if (eventId) params.set('event', eventId); else params.delete('event');
    const qs = params.toString();
    window.history.replaceState({}, '', `/secretary${qs ? `?${qs}` : ''}`);
  };

  const handleSelectEvent = (event, origin = 'events') => {
    setSelectedEvent(event);
    setEntriesOrigin(origin);
    setView('entries');
    setEventUrlParam(event.CalendarID);
  };

  const handleBackToEvents = () => {
    setSelectedEvent(null);
    setView(FINANCIAL_ROLES.has(role) && entriesOrigin === 'dashboard' ? 'financial-dashboard' : 'events');
    setEventUrlParam(null);
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  const portalTitle = PORTAL_TITLES[role] || PORTAL_TITLES.secretary;

  return (
    <div className="secretary-portal App">
      <header className="App-header">
        <h1>🏍️ NHCA {portalTitle}</h1>
        <p>National Hillclimb Association – Event Entry Management</p>
      </header>

      {/* Nav bar – shown when logged in */}
      {sessionToken && (
        <nav className="secretary-nav">
          <div className="secretary-nav-inner">
            {(view === 'entries' || view === 'vctargets' || view === 'vcrecords' || view === 'ferecords') && (
              <button
                className="secretary-nav-btn"
                onClick={handleBackToEvents}
              >
                {FINANCIAL_ROLES.has(role) && entriesOrigin === 'dashboard' ? '← Back to Consolidated View' : '← My Events'}
              </button>
            )}
            {view === 'events' && FINANCIAL_ROLES.has(role) && (
              <button
                className="secretary-nav-btn"
                onClick={() => setView('financial-dashboard')}
              >
                ← Back to Consolidated View
              </button>
            )}
            <span className="spacer" />
            {email && <span className="email-badge">{email}</span>}
            <button className="secretary-nav-btn" onClick={handleLogout}>
              Log Out
            </button>
          </div>
        </nav>
      )}

      {/* Verifying magic link */}
      {verifying && (
        <div className="App-main" style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1rem' }}>
          <Spinner />
        </div>
      )}

      {/* Magic link error */}
      {verifyError && !verifying && (
        <div className="App-main" style={{ maxWidth: '1100px', margin: '0 auto', padding: '2rem 1rem' }}>
          <div className="error-message">
            <h3>⚠️ Login Failed</h3>
            <p>{verifyError}</p>
            <button onClick={() => { setVerifyError(null); setView('login'); }}>
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Main content */}
      {!verifying && !verifyError && (
        <main className="secretary-main">
          {view === 'login' && (
            <LoginScreen
              subclass={urlSubclass}
              initialEmail={email}
              sessionExpired={sessionExpired}
              onVerified={(tok, em) => {
                setSessionToken(tok);
                setEmail(em);
                setSessionExpired(false);
                setView(FINANCIAL_ROLES.has(role) ? 'financial-dashboard' : 'events');
              }}
            />
          )}

          {view === 'events' && sessionToken && (
            <EventList
              sessionToken={sessionToken}
              role={role}
              onSelectEvent={handleSelectEvent}
              initialEventId={initialEventId}
              onManageTargets={() => setView('vctargets')}
              onManageRecords={() => setView('vcrecords')}
              onManageFeRecords={() => setView('ferecords')}
            />
          )}

          {view === 'financial-dashboard' && sessionToken && (
            <FinancialDashboard
              sessionToken={sessionToken}
              onSelectEvent={(ev) => handleSelectEvent(ev, 'dashboard')}
              onBrowseEvents={() => setView('events')}
            />
          )}

          {view === 'entries' && sessionToken && selectedEvent && (
            FINANCIAL_ROLES.has(role)
              ? <FinancialEntryList
                  event={selectedEvent}
                  sessionToken={sessionToken}
                  onBack={handleBackToEvents}
                  backLabel={entriesOrigin === 'dashboard' ? '← Back to Consolidated View' : '← Back to Events'}
                />
              : <EntryList
                  event={selectedEvent}
                  sessionToken={sessionToken}
                  role={role}
                  onBack={handleBackToEvents}
                />
          )}

          {view === 'vctargets' && sessionToken && (
            <VCTargetsEditor
              sessionToken={sessionToken}
              onBack={handleBackToEvents}
            />
          )}

          {view === 'vcrecords' && sessionToken && (
            <RecordsEditor
              sessionToken={sessionToken}
              classes={VC_CLASSES}
              apiPath="/api/secretary/vcrecords"
              title="VC Records"
              icon="🏆"
              description="Course records for Vintage-Classic classes. Multiple rows per hill/class show the record history; the most recent date is the current record."
            />
          )}

          {view === 'ferecords' && sessionToken && (
            <RecordsEditor
              sessionToken={sessionToken}
              classes={FE_CLASSES}
              apiPath="/api/secretary/ferecords"
              title="FE Records"
              icon="🏅"
              description="Course records for Forgotten Era classes. Multiple rows per hill/class show the record history; the most recent date is the current record."
            />
          )}
        </main>
      )}

      <footer className="App-footer">
        <p>NHCA Secretary Portal | Entry data updated in real-time</p>
      </footer>
    </div>
  );
}
